<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    fullscreen
    transition="dialog-bottom-transition"
  >
    <div class="dash">
      <!-- ================= HEADER ================= -->
      <header class="dash-header">
        <div class="d-flex align-items-center gap-3">
          <div class="dash-header-icon">
            <i class="fa-solid fa-chart-line"></i>
          </div>
          <div>
            <h2 class="dash-title">Loyiha analitikasi</h2>
            <p class="dash-subtitle">
              {{ workspaceName || 'Barcha loyihalar' }} ·
              {{ tasks.length }} ta topshiriq
            </p>
          </div>
        </div>

        <div class="dash-header-actions">
          <button class="dash-ghost-btn" @click="fetchStats" :disabled="loading" title="Yangilash">
            <i class="fa-solid fa-rotate" :class="{ 'fa-spin': loading }"></i>
          </button>
          <button class="dash-close" @click="$emit('update:modelValue', false)">
            <i class="fa fa-times"></i>
          </button>
        </div>
      </header>

      <!-- ================= LOADING / EMPTY ================= -->
      <div v-if="loading" class="dash-state">
        <div class="spinner-border text-primary" role="status"></div>
        <span>Ma'lumotlar yuklanmoqda...</span>
      </div>

      <div v-else-if="infoMessage" class="dash-state">
        <i class="fa-regular fa-folder-open"></i>
        <span>{{ infoMessage }}</span>
      </div>

      <div v-else-if="!tasks.length" class="dash-state">
        <i class="fa-regular fa-folder-open"></i>
        <span>Bu loyihada topshiriqlar mavjud emas</span>
      </div>

      <!-- ================= BODY ================= -->
      <div v-else class="dash-body">
        <!-- KPI CARDS -->
        <section class="kpi-grid">
          <div class="kpi-card">
            <div class="kpi-icon" style="background: rgba(99,102,241,.12); color:#6366f1;">
              <i class="fa-solid fa-layer-group"></i>
            </div>
            <div class="kpi-meta">
              <div class="kpi-value">{{ kpi.total }}</div>
              <div class="kpi-label">Jami topshiriq</div>
            </div>
          </div>

          <div class="kpi-card">
            <div class="kpi-icon" style="background: rgba(16,185,129,.12); color:#10b981;">
              <i class="fa-solid fa-circle-check"></i>
            </div>
            <div class="kpi-meta">
              <div class="kpi-value">
                {{ kpi.completed }}
                <span class="kpi-sub">{{ kpi.completionPct }}%</span>
              </div>
              <div class="kpi-label">Qabul qilindi</div>
              <div class="kpi-progress">
                <div class="kpi-progress-fill" :style="{ width: kpi.completionPct + '%', background: '#10b981' }"></div>
              </div>
            </div>
          </div>

          <div class="kpi-card">
            <div class="kpi-icon" style="background: rgba(245,158,11,.12); color:#f59e0b;">
              <i class="fa-solid fa-spinner"></i>
            </div>
            <div class="kpi-meta">
              <div class="kpi-value">{{ kpi.inProgress }}</div>
              <div class="kpi-label">Jarayonda</div>
            </div>
          </div>

          <div class="kpi-card" :class="{ 'kpi-danger': kpi.overdue > 0 }">
            <div class="kpi-icon" style="background: rgba(239,68,68,.12); color:#ef4444;">
              <i class="fa-solid fa-triangle-exclamation"></i>
            </div>
            <div class="kpi-meta">
              <div class="kpi-value">{{ kpi.overdue }}</div>
              <div class="kpi-label">Muddati o'tgan</div>
            </div>
          </div>

          <div class="kpi-card">
            <div class="kpi-icon" style="background: rgba(59,130,246,.12); color:#3b82f6;">
              <i class="fa-solid fa-hourglass-half"></i>
            </div>
            <div class="kpi-meta">
              <div class="kpi-value">{{ kpi.avgCompletionDays }}<small> kun</small></div>
              <div class="kpi-label">O'rtacha bajarilish</div>
            </div>
          </div>

          <div class="kpi-card">
            <div class="kpi-icon" style="background: rgba(139,92,246,.12); color:#8b5cf6;">
              <i class="fa-solid fa-users"></i>
            </div>
            <div class="kpi-meta">
              <div class="kpi-value">{{ workload.length }}</div>
              <div class="kpi-label">Faol xodimlar</div>
            </div>
          </div>
        </section>

        <!-- TABS -->
        <nav class="dash-tabs">
          <button
            v-for="t in tabs"
            :key="t.key"
            class="dash-tab"
            :class="{ active: activeTab === t.key }"
            @click="activeTab = t.key"
          >
            <i :class="t.icon"></i> {{ t.label }}
          </button>
        </nav>

        <!-- ============ TAB: UMUMIY ============ -->
        <div v-show="activeTab === 'overview'" class="tab-pane">
          <div class="grid-2">
            <!-- Status donut -->
            <div class="panel">
              <div class="panel-head">
                <h3>Status bo'yicha taqsimot</h3>
                <span class="panel-hint">Batafsil uchun bosing</span>
              </div>
              <div class="donut-wrap">
                <svg viewBox="0 0 200 200" class="donut-svg">
                  <circle cx="100" cy="100" :r="donutR" fill="none" stroke="#eef2f7" :stroke-width="26" />
                  <circle
                    v-for="seg in donutSegments"
                    :key="seg.label"
                    cx="100" cy="100" :r="donutR"
                    fill="none"
                    :stroke="seg.color"
                    :stroke-width="26"
                    :stroke-dasharray="seg.dash"
                    :stroke-dashoffset="seg.offset"
                    transform="rotate(-90 100 100)"
                    stroke-linecap="butt"
                    class="donut-seg"
                    :class="{ 'donut-seg--dim': selectedStatus && selectedStatus !== seg.label }"
                    @click="selectStatus(seg.label)"
                  />
                  <text x="100" y="94" text-anchor="middle" class="donut-center-num">
                    {{ selectedStatus ? (statusCounts[selectedStatus] || 0) : kpi.total }}
                  </text>
                  <text x="100" y="114" text-anchor="middle" class="donut-center-label">
                    {{ selectedStatus || 'topshiriq' }}
                  </text>
                </svg>
                <div class="donut-legend">
                  <div
                    v-for="seg in donutSegments"
                    :key="seg.label"
                    class="legend-item legend-clickable"
                    :class="{ active: selectedStatus === seg.label }"
                    @click="selectStatus(seg.label)"
                  >
                    <span class="legend-dot" :style="{ background: seg.color }"></span>
                    <span class="legend-label">{{ seg.label }}</span>
                    <span class="legend-value">{{ seg.value }} <small>({{ seg.pct }}%)</small></span>
                  </div>
                </div>
              </div>

              <!-- Drilldown: tanlangan status bo'yicha topshiriqlar -->
              <transition name="fade">
                <div v-if="selectedStatus" class="drilldown">
                  <div class="drilldown-head">
                    <span>
                      <span class="legend-dot" :style="{ background: statusColor(selectedStatus) }"></span>
                      <b>{{ selectedStatus }}</b> — {{ drilldownTasks.length }} ta topshiriq
                    </span>
                    <button class="drilldown-close" @click="selectedStatus = null">
                      <i class="fa fa-times"></i>
                    </button>
                  </div>
                  <div class="drilldown-list">
                    <div v-for="d in drilldownTasks" :key="d.id" class="drilldown-cell">
                    <div
                      class="drilldown-item"
                      :class="{ 'is-overdue': d.overdue, 'has-subs': d.hasSubs, 'is-expanded': expandedTaskId === d.id }"
                      @click="d.hasSubs && toggleDrilldownTask(d.id)"
                    >
                      <div class="drilldown-info">
                        <span class="drilldown-name text-truncate" :title="d.name">
                          <span class="dd-id">#{{ d.id }}</span>
                          <!-- Subtaskli topshiriq belgisi -->
                          <span v-if="d.hasSubs" class="dd-sub-marker" :title="d.subsTotal + ' ta subtask — batafsil uchun bosing'">
                            <i class="fa-solid fa-diagram-project"></i>
                          </span>
                          {{ d.name }}
                        </span>

                        <!-- Barcha ma'lumot bitta qatorda, belgilar bilan -->
                        <div class="dd-meta">
                          <span class="dd-chip dd-chip--exec" :title="'Ijrochi: ' + (d.ijroNamesFull || 'biriktirilmagan')">
                            <i class="fa-solid fa-user-check"></i>
                            <b class="dd-key">Ijrochi:</b> {{ d.ijroNames || 'yo‘q' }}
                          </span>

                          <span class="dd-chip dd-chip--author" :title="'Muallif: ' + d.creatorNameFull">
                            <i class="fa-regular fa-pen-to-square"></i>
                            <b class="dd-key">Muallif:</b> {{ d.creatorName }}
                          </span>

                          <span class="dd-chip" title="Topshiriq tushirilgan vaqti">
                            <i class="fa-regular fa-clock"></i> {{ d.createdLabel }}
                          </span>

                          <span class="dd-chip" title="Bajarish muddati">
                            <i class="fa-regular fa-calendar"></i> {{ d.deadlineLabel }}
                            <span class="dd-days" :class="'dd-days--' + d.daysTone">{{ d.daysLabel }}</span>
                          </span>

                          <span
                            v-if="d.subsTotal"
                            class="dd-chip dd-chip--sub"
                            :title="'Subtasklar: ' + d.subsDone + ' / ' + d.subsTotal + ' bajarilgan'"
                          >
                            <i class="fa-solid fa-list-check"></i> {{ d.subsDone }}/{{ d.subsTotal }} · {{ d.subsPct }}%
                          </span>

                          <span v-if="d.infoCount" class="dd-chip" :title="d.infoCount + ' xodim ma’lumot uchun'">
                            <i class="fa-regular fa-eye"></i> {{ d.infoCount }}
                          </span>

                          <span v-if="d.comments" class="dd-chip" :title="d.comments + ' ta izoh'">
                            <i class="fa-regular fa-comment"></i> {{ d.comments }}
                          </span>

                          <span v-if="d.isChecked" class="dd-chip dd-chip--ok" title="Tekshirilgan">
                            <i class="fa-solid fa-circle-check"></i>
                          </span>
                        </div>
                      </div>

                      <div class="drilldown-assignees">
                        <template v-if="d.ijroMembers.length">
                          <img
                            v-for="a in d.ijroMembers.slice(0, 4)"
                            :key="a.user_id"
                            :src="avatarUrl(a.avatar)"
                            :title="a.full_name"
                            class="drilldown-avatar"
                            @error="onImgErr"
                          />
                          <span v-if="d.ijroMembers.length > 4" class="drilldown-more">+{{ d.ijroMembers.length - 4 }}</span>
                        </template>
                        <span v-else class="td-empty">—</span>
                        <i v-if="d.hasSubs" class="fa-solid fa-chevron-down dd-expand-icon" :class="{ open: expandedTaskId === d.id }"></i>
                      </div>
                    </div>

                    <!-- Ochiluvchi subtask chartlari -->
                    <transition name="expand">
                      <div v-if="expandedTaskId === d.id && d.hasSubs" class="subchart" @click.stop>
                        <div class="subchart-donut">
                          <svg viewBox="0 0 70 70">
                            <circle cx="35" cy="35" :r="d.subChart.r" fill="none" stroke="#eef2f7" stroke-width="8" />
                            <circle
                              v-for="seg in d.subChart.segments"
                              :key="seg.label"
                              cx="35" cy="35" :r="d.subChart.r"
                              fill="none"
                              :stroke="seg.color"
                              stroke-width="8"
                              :stroke-dasharray="seg.dash"
                              :stroke-dashoffset="seg.offset"
                              transform="rotate(-90 35 35)"
                            />
                            <text x="35" y="33" text-anchor="middle" class="subchart-num">{{ d.subsDone }}/{{ d.subsTotal }}</text>
                            <text x="35" y="45" text-anchor="middle" class="subchart-lbl">bajarildi</text>
                          </svg>
                          <div class="subchart-legend">
                            <div v-for="seg in d.subChart.segments" :key="seg.label" class="subchart-leg-item">
                              <span class="legend-dot" :style="{ background: seg.color }"></span>
                              {{ seg.label }} <b>{{ seg.value }}</b>
                            </div>
                          </div>
                        </div>

                        <div class="subchart-list">
                          <div v-for="s in d.subList" :key="s.id" class="subchart-row">
                            <i
                              class="subchart-check"
                              :class="s.done ? 'fa-solid fa-circle-check done' : 'fa-regular fa-circle'"
                            ></i>
                            <div class="subchart-row-info">
                              <span class="subchart-row-name text-truncate" :title="s.name">{{ s.name }}</span>
                              <div class="subchart-row-meta">
                                <span class="subchart-status" :class="'st-' + s.statusLabel.toLowerCase().replace(/\s/g,'')">{{ s.statusLabel }}</span>
                                <span v-if="s.deadlineLabel" class="subchart-date"><i class="fa-regular fa-calendar"></i> {{ s.deadlineLabel }}</span>
                              </div>
                            </div>
                            <div class="subchart-avatars">
                              <img
                                v-for="a in s.assignees.slice(0, 3)"
                                :key="a.user_id"
                                :src="avatarUrl(a.avatar)"
                                :title="a.fullName + ' (' + a.process + ')'"
                                class="subchart-avatar"
                                @error="onImgErr"
                              />
                              <span v-if="s.assignees.length > 3" class="drilldown-more">+{{ s.assignees.length - 3 }}</span>
                              <span v-if="!s.assignees.length" class="td-empty">—</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </transition>
                    </div>
                  </div>
                </div>
              </transition>
            </div>

            <!-- Completion dynamics line -->
            <div class="panel">
              <div class="panel-head">
                <h3>Bajarilish dinamikasi</h3>
                <div class="line-legend">
                  <span><i class="dot" style="background:#3b82f6"></i> Yaratilgan</span>
                  <span><i class="dot" style="background:#10b981"></i> Yopilgan</span>
                </div>
              </div>
              <div v-if="timeline.buckets.length" class="line-chart">
                <svg :viewBox="`0 0 ${lineW} ${lineH}`" preserveAspectRatio="none" class="line-svg">
                  <line v-for="g in 4" :key="'g'+g"
                    :x1="0" :x2="lineW"
                    :y1="(lineH - linePad) / 4 * g" :y2="(lineH - linePad) / 4 * g"
                    stroke="#eef2f7" stroke-width="1" />
                  <polyline :points="timeline.createdPoints" fill="none" stroke="#3b82f6" stroke-width="2.5" />
                  <polyline :points="timeline.completedPoints" fill="none" stroke="#10b981" stroke-width="2.5" />
                  <g v-for="(b, i) in timeline.buckets" :key="'cd'+i + b.key">
                    <circle :cx="timeline.x(i)" :cy="timeline.yCreated(i)" r="3" fill="#3b82f6" />
                    <circle :cx="timeline.x(i)" :cy="timeline.yCompleted(i)" r="3" fill="#10b981" />
                  </g>
                </svg>
                <div class="line-x-labels">
                  <span v-for="(b, i) in timeline.buckets" :key="'lx'+i">{{ b.short }}</span>
                </div>
              </div>
              <div v-else class="panel-empty">Ma'lumot yetarli emas</div>
            </div>
          </div>

          <div class="grid-2">
            <!-- Status x deadline -->
            <div class="panel">
              <div class="panel-head">
                <h3>Status × Muddat kesishmasi</h3>
                <div class="line-legend">
                  <span><i class="dot" style="background:#94a3b8"></i> Status rangi = muddatida</span>
                  <span><i class="dot dot-striped" style="background:#ef4444"></i> Kechikkan</span>
                </div>
              </div>
              <div class="sxd-list">
                <div v-for="s in statusDeadline" :key="s.status" class="sxd-row">
                  <span class="sxd-status" :style="{ color: s.color }">
                    <span class="legend-dot" :style="{ background: s.color }"></span>
                    {{ s.status }}
                  </span>
                  <div class="sxd-track">
                    <div class="sxd-bar" :style="{ width: s.widthPct + '%' }">
                      <!-- Muddatida — status o'z rangida -->
                      <div class="sxd-ontime" :style="{ width: s.onTimePct + '%', background: s.color }" :title="s.onTime + ' muddatida'"></div>
                      <!-- Kechikkan — qizil, ajratib turadi -->
                      <div class="sxd-overdue" :style="{ width: s.overduePct + '%' }" :title="s.overdue + ' kechikkan'"></div>
                    </div>
                  </div>
                  <span class="sxd-nums">
                    <b>{{ s.total }}</b>
                    <span v-if="s.overdue" class="sxd-over-tag">{{ s.overdue }} kech</span>
                  </span>
                </div>
              </div>
            </div>

            <!-- Employee efficiency (loyiha o'rniga) -->
            <div class="panel">
              <div class="panel-head">
                <h3>Ijrochilar samaradorligi</h3>
                <span class="panel-hint">Tasdiqlangan + Qabul qilingan / biriktirilgan</span>
              </div>
              <div v-if="employeeEfficiency.length" class="eff-list">
                <div v-for="e in employeeEfficiency" :key="e.user_id" class="eff-row">
                  <img :src="avatarUrl(e.avatar)" :alt="e.name" class="eff-avatar" @error="onImgErr" />
                  <div class="eff-info">
                    <div class="eff-top">
                      <span class="eff-name text-truncate" :title="e.name">{{ e.name }}</span>
                      <span class="eff-rate" :style="{ color: effColor(e.rate) }">{{ e.rate }}%</span>
                    </div>
                    <div class="eff-track">
                      <div class="eff-fill" :style="{ width: e.rate + '%', background: effColor(e.rate) }"></div>
                    </div>
                    <div class="eff-meta">
                      <span><i class="fa-solid fa-circle-check"></i> {{ e.done }}/{{ e.assigned }}</span>
                      <span v-if="e.subAssigned" class="eff-sub" :title="e.subAssigned + ' ta subtask ijrochiligi'"><i class="fa-solid fa-diagram-project"></i> {{ e.subAssigned }} subtask</span>
                      <span v-if="e.overdue" class="eff-overdue"><i class="fa-solid fa-triangle-exclamation"></i> {{ e.overdue }} kech</span>
                      <span v-if="e.avgCycle !== null"><i class="fa-solid fa-hourglass-half"></i> {{ e.avgCycle }} kun</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="panel-empty">Ijrochilar mavjud emas</div>
            </div>
          </div>

          <!-- Subtasklar progressi -->
          <div class="panel">
            <div class="panel-head">
              <h3>Subtasklar progressi</h3>
              <div class="subtask-summary" v-if="subtasksData.totalSubs">
                <span class="subtask-chip">{{ subtasksData.tasksWithSubs }} ta topshiriqda</span>
                <span class="subtask-chip">{{ subtasksData.doneSubs }}/{{ subtasksData.totalSubs }} bajarilgan</span>
                <span class="subtask-chip subtask-chip--accent">{{ subtasksData.pct }}%</span>
              </div>
            </div>

            <div v-if="subtasksData.tasks.length" class="sub-grid">
              <div v-for="t in subtasksData.tasks" :key="t.id" class="sub-card">
                <div class="sub-card-top">
                  <span class="sub-name text-truncate" :title="t.name">{{ t.name }}</span>
                  <span class="status-pill" :style="{ background: statusColor(t.status) + '22', color: statusColor(t.status) }">
                    {{ t.status }}
                  </span>
                </div>
                <div class="sub-progress">
                  <div class="sub-progress-track">
                    <div
                      class="sub-progress-fill"
                      :style="{ width: t.pct + '%', background: t.pct === 100 ? '#10b981' : '#6366f1' }"
                    ></div>
                  </div>
                  <span class="sub-progress-label">{{ t.done }}/{{ t.total }} · {{ t.pct }}%</span>
                </div>
              </div>
            </div>
            <div v-else class="panel-empty">
              <i class="fa-solid fa-list-check" style="font-size:22px; color:#cbd5e1; margin-right:6px;"></i>
              Hech bir topshiriqda subtask mavjud emas
            </div>
          </div>
        </div>

        <!-- ============ TAB: XODIMLAR ============ -->
        <div v-show="activeTab === 'employees'" class="tab-pane">
          <!-- PODIUM: eng samarali ijrochilar -->
          <div v-if="topExecutors.length" class="podium-panel">
            <div class="podium-head">
              <div>
                <h3 class="podium-title"><i class="fa-solid fa-trophy"></i> Eng samarali ijrochilar</h3>
                <p class="podium-sub">Tasdiqlangan va qabul qilingan topshiriqlar bo'yicha</p>
              </div>
            </div>

            <div class="podium">
              <div
                v-for="p in topExecutors"
                :key="p.user_id"
                class="podium-spot"
                :class="'rank-' + p.rank"
              >
                <div class="podium-badge">
                  <i v-if="p.rank === 1" class="fa-solid fa-crown"></i>
                  <span v-else>{{ p.rank }}</span>
                </div>
                <div class="podium-avatar-ring">
                  <img :src="avatarUrl(p.avatar)" :alt="p.name" class="podium-avatar" @error="onImgErr" />
                </div>
                <div class="podium-name text-truncate" :title="p.name">{{ p.name }}</div>
                <div class="podium-done">{{ p.done }} <small>ta bajarilgan</small></div>
                <div class="podium-stats">
                  <span class="podium-rate" :style="{ color: effColor(p.rate) }">{{ p.rate }}%</span>
                  <span class="podium-assigned">
                    {{ p.assigned }} ta jami<template v-if="p.subAssigned"> · +{{ p.subAssigned }} subtask</template>
                  </span>
                </div>
                <div class="podium-stand">
                  <span class="podium-place">{{ p.rank }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="panel">
            <div class="panel-head">
              <h3>Xodimlar bo'yicha yuklama</h3>
              <div class="line-legend">
                <span><i class="dot" style="background:#10b981"></i> Ijro</span>
                <span><i class="dot dot-striped" style="background:#10b981"></i> Subtask ijro</span>
                <span><i class="dot" style="background:#94a3b8"></i> Malumot</span>
                <span class="overload-hint"><i class="dot" style="background:#ef4444"></i> Ortiqcha yuk (3+ Ijro)</span>
              </div>
            </div>

            <div class="workload-list">
              <div
                v-for="w in workload"
                :key="w.user_id"
                class="workload-row"
                :class="{ overloaded: w.overloaded }"
              >
                <div class="workload-user">
                  <img :src="avatarUrl(w.avatar)" :alt="w.name" class="workload-avatar" @error="onImgErr" />
                  <div class="workload-user-info">
                    <span class="workload-name text-truncate" :title="w.name">{{ w.name }}</span>
                    <span v-if="w.overloaded" class="overload-badge">
                      <i class="fa-solid fa-fire"></i> Ortiqcha yuk
                    </span>
                  </div>
                </div>

                <div class="workload-bar-wrap">
                  <div class="workload-bar">
                    <!-- Task ijro -->
                    <div
                      v-if="w.ijro"
                      class="workload-seg"
                      :style="{ width: (w.ijro / workloadMax * 100) + '%', background: w.overloaded ? '#ef4444' : '#10b981' }"
                      :title="w.ijro + ' ta task Ijro'"
                    >{{ w.ijro }}</div>
                    <!-- Subtask ijro — chiziqli (ajratilgan) -->
                    <div
                      v-if="w.subIjro"
                      class="workload-seg workload-seg--sub"
                      :style="{ width: (w.subIjro / workloadMax * 100) + '%', '--seg-color': w.overloaded ? '#ef4444' : '#10b981' }"
                      :title="w.subIjro + ' ta subtask Ijro'"
                    >{{ w.subIjro }}</div>
                    <!-- Malumot -->
                    <div
                      v-if="w.malumot"
                      class="workload-seg"
                      :style="{ width: (w.malumot / workloadMax * 100) + '%', background: '#94a3b8' }"
                      :title="w.malumot + ' ta task Malumot'"
                    >{{ w.malumot }}</div>
                    <!-- Subtask malumot -->
                    <div
                      v-if="w.subMalumot"
                      class="workload-seg workload-seg--sub"
                      :style="{ width: (w.subMalumot / workloadMax * 100) + '%', '--seg-color': '#94a3b8' }"
                      :title="w.subMalumot + ' ta subtask Malumot'"
                    >{{ w.subMalumot }}</div>
                  </div>
                </div>

                <div class="workload-stats">
                  <span class="wl-chip wl-ijro">
                    {{ w.ijro }} Ijro
                    <b v-if="w.subIjro" class="wl-sub-tag" :title="w.subIjro + ' ta subtaskdan'">+{{ w.subIjro }}</b>
                  </span>
                  <span class="wl-chip wl-malumot">
                    {{ w.malumot }} Malumot
                    <b v-if="w.subMalumot" class="wl-sub-tag" :title="w.subMalumot + ' ta subtaskdan'">+{{ w.subMalumot }}</b>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Employee x status matrix -->
          <div class="panel">
            <div class="panel-head">
              <h3>Xodim × Status matritsasi</h3>
              <span class="panel-hint">Ijrochilarning status taqsimoti</span>
            </div>
            <div class="matrix-scroll">
              <table class="matrix-table">
                <thead>
                  <tr>
                    <th class="matrix-corner">Xodim</th>
                    <th v-for="s in statusList" :key="s">{{ s }}</th>
                    <th class="matrix-total">Jami</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in employeeStatusMatrix" :key="row.user_id">
                    <td class="matrix-name">
                      <img :src="avatarUrl(row.avatar)" class="matrix-avatar" @error="onImgErr" />
                      <span class="text-truncate" :title="row.name">{{ row.name }}</span>
                    </td>
                    <td
                      v-for="s in statusList"
                      :key="s"
                      class="matrix-cell"
                      :style="cellStyle(row.counts[s] || 0, s)"
                    >
                      {{ row.counts[s] || 0 }}
                    </td>
                    <td class="matrix-total-cell">{{ row.total }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- ============ TAB: MUDDATLAR ============ -->
        <div v-show="activeTab === 'deadlines'" class="tab-pane">
          <div class="panel">
            <div class="panel-head">
              <h3>Muddatlar monitoringi (Gantt)</h3>
              <div class="line-legend">
                <span><i class="dot" style="background:#3b82f6"></i> Yangi</span>
                <span><i class="dot" style="background:#f59e0b"></i> Jarayonda</span>
                <span><i class="dot" style="background:#10b981"></i> Qabul qilindi</span>
                <span><i class="dot" style="background:#ef4444"></i> Muddati o'tgan</span>
              </div>
            </div>

            <div class="gantt">
              <div class="gantt-scale">
                <span v-for="(m, i) in gantt.months" :key="i" class="gantt-month" :style="{ left: m.pos + '%' }">
                  {{ m.label }}
                </span>
              </div>
              <div class="gantt-rows">
                <div class="gantt-today" :style="{ left: gantt.todayPos + '%' }" title="Bugun">
                  <span class="gantt-today-label">Bugun</span>
                </div>
                <div v-for="row in gantt.rows" :key="row.id" class="gantt-row">
                  <div class="gantt-row-label text-truncate" :title="row.name">{{ row.name }}</div>
                  <div class="gantt-track">
                    <div
                      class="gantt-bar"
                      :class="{ 'gantt-overdue': row.overdue }"
                      :style="{ left: row.left + '%', width: row.width + '%', background: row.color }"
                      :title="row.tooltip"
                    >
                      <span class="gantt-bar-text">{{ row.endLabel }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ============ TAB: JADVAL ============ -->
        <div v-show="activeTab === 'table'" class="tab-pane">
          <div class="panel tbl-panel">
            <!-- Toolbar -->
            <div class="tbl2-toolbar">
              <div class="tbl2-search">
                <i class="fa fa-magnifying-glass"></i>
                <input v-model="tblSearch" type="text" placeholder="Topshiriq nomi..." />
                <button v-if="tblSearch" @click="tblSearch = ''"><i class="fa fa-xmark"></i></button>
              </div>

              <div class="tbl2-field">
                <label>Status</label>
                <select v-model="tblStatus" class="tbl2-select">
                  <option value="">Barchasi</option>
                  <option v-for="s in statusList" :key="s" :value="s">{{ s }}</option>
                </select>
              </div>

              <div class="tbl2-field tbl2-field--grow">
                <label>Xodim</label>
                <select v-model="tblEmployee" class="tbl2-select">
                  <option value="">Barcha xodimlar</option>
                  <option v-for="e in employeeOptions" :key="e.user_id" :value="e.user_id">{{ e.name }}</option>
                </select>
              </div>

              <!-- Rol segmenti — xodim tanlanganda faol -->
              <div class="tbl2-field">
                <label>Rol bo'yicha</label>
                <div class="role-seg" :class="{ disabled: !tblEmployee }">
                  <button
                    v-for="opt in [
                      { key: 'all', label: 'Ikkalasi', icon: 'fa-users' },
                      { key: 'ijro', label: 'Ijrochi', icon: 'fa-user-check' },
                      { key: 'author', label: 'Muallif', icon: 'fa-pen-nib' },
                    ]"
                    :key="opt.key"
                    class="role-seg-btn"
                    :class="{ active: tblRole === opt.key }"
                    :disabled="!tblEmployee"
                    @click="tblRole = opt.key"
                  >
                    <i class="fa-solid" :class="opt.icon"></i> {{ opt.label }}
                  </button>
                </div>
              </div>

              <!-- Muddat o'tgan bo'yicha filter -->
              <div class="tbl2-field">
                <label>Muddat o'tgan</label>
                <div class="role-seg">
                  <button
                    v-for="opt in [
                      { key: 'all', label: 'Barchasi', icon: 'fa-list' },
                      { key: 'task', label: 'Task', icon: 'fa-clipboard-check' },
                      { key: 'subtask', label: 'Subtask', icon: 'fa-diagram-project' },
                    ]"
                    :key="opt.key"
                    class="role-seg-btn"
                    :class="{ active: tblOverdue === opt.key, 'seg-danger': tblOverdue === opt.key && opt.key !== 'all' }"
                    @click="tblOverdue = opt.key"
                  >
                    <i class="fa-solid" :class="opt.icon"></i> {{ opt.label }}
                  </button>
                </div>
              </div>

              <!-- Excel Export -->
              <div class="tbl2-field" style="margin-left: auto;">
                <label>&nbsp;</label>
                <button 
                  style="color: #10b981; border: 1px solid #10b981; height: 40px; padding: 0 16px; border-radius: 11px; display: flex; align-items: center; justify-content: center; gap: 8px; background: rgba(16,185,129,0.1); font-weight: 600; font-size: 13px; white-space: nowrap; cursor: pointer; transition: 0.2s;" 
                  @click="exportTableToExcel" 
                  title="Jadvalni Excel (CSV) faylida yuklab olish"
                >
                  <i class="fa-solid fa-file-excel" style="font-size: 14px;"></i> Excel
                </button>
              </div>

              <!-- <span class="tbl2-count" style="align-self: flex-end; margin-bottom: 12px; margin-left: 10px;">
                <b>{{ tableRows.length }}</b> ta
              </span> -->
            </div>

            <!-- Jadval -->
            <div class="tbl2-scroll">
              <table class="tbl2">
                <thead>
                  <tr>
                    <th class="tbl2-id">Tartib</th>
                    <th class="tbl2-num">Topshiriq №</th>
                    <th @click="sortBy('task_name')" class="sortable">Topshiriq <i :class="sortIcon('task_name')"></i></th>
                    <th @click="sortBy('status')" class="sortable">Status <i :class="sortIcon('status')"></i></th>
                    <th>Ijrochi</th>
                    <th>Muallif</th>
                    <th @click="sortBy('deadline')" class="sortable">Muddat <i :class="sortIcon('deadline')"></i></th>
                    <th @click="sortBy('daysLeft')" class="sortable">Qolgan <i :class="sortIcon('daysLeft')"></i></th>
                    <th>Subtask</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(r, idx) in tableRows" :key="r.id">
                  <tr :class="{ 'tbl2-overdue': r.overdue, 'tbl2-expanded': isTblExpanded(r.id) }">
                    <td class="tbl2-id">{{ idx + 1 }}</td>
                    <td class="tbl2-num"><span class="tbl2-num-badge">{{ r.taskNumber }}</span></td>
                    <td class="tbl2-name">
                      <span
                        class="tbl2-status-bar"
                        :style="{ background: statusColor(r.status) }"
                      ></span>
                      <span class="text-truncate" :title="r.task_name">{{ r.task_name }}</span>
                    </td>
                    <td>
                      <span class="status-pill" :style="{ background: statusColor(r.status) + '1f', color: statusColor(r.status) }">
                        {{ r.status }}
                      </span>
                    </td>
                    <td>
                      <div v-if="r.ijroMembers.length" class="tbl2-ijro" :title="r.ijroFull">
                        <div class="tbl2-users">
                          <img
                            v-for="a in r.ijroMembers.slice(0, 3)"
                            :key="a.user_id"
                            :src="avatarUrl(a.avatar)"
                            :title="a.full_name"
                            class="tbl2-avatar"
                            @error="onImgErr"
                          />
                          <span v-if="r.ijroMembers.length > 3" class="tbl2-more">+{{ r.ijroMembers.length - 3 }}</span>
                        </div>
                        <span class="tbl2-ijro-name text-truncate">{{ r.ijroLabel }}</span>
                      </div>
                      <span v-else class="td-empty">—</span>
                    </td>
                    <td>
                      <div class="tbl2-author" :title="r.authorFull">
                        <img :src="avatarUrl(r.authorAvatar)" class="tbl2-avatar" @error="onImgErr" />
                        <span class="text-truncate">{{ r.authorName }}</span>
                      </div>
                    </td>
                    <td class="tbl2-date">{{ r.deadlineLabel }}</td>
                    <td>
                      <span class="days-badge" :class="daysClass(r)">{{ r.daysLabel }}</span>
                    </td>
                    <td>
                      <button
                        v-if="r.subtaskTotal"
                        type="button"
                        class="subtask-toggle"
                        :class="{ open: isTblExpanded(r.id), full: r.subtaskDone === r.subtaskTotal }"
                        :title="r.subtaskTotal + ' ta subtask — ochish uchun bosing'"
                        @click="toggleTblRow(r.id)"
                      >
                        <i class="fa-solid fa-diagram-project subtask-toggle-ico"></i>
                        <span class="subtask-toggle-count">{{ r.subtaskDone }}/{{ r.subtaskTotal }}</span>
                        <i class="fa-solid fa-chevron-down subtask-toggle-arrow"></i>
                      </button>
                      <span v-else class="td-empty">—</span>
                    </td>
                  </tr>
                  <tr v-if="isTblExpanded(r.id)" class="tbl2-subrow">
                    <td :colspan="9">
                      <div class="subpanel">
                        <!-- Sarlavha: mini-donut + statistikalar + progress -->
                        <div class="subpanel-head">
                          <div class="subpanel-donut">
                            <svg viewBox="0 0 70 70">
                              <circle cx="35" cy="35" :r="r.subChart.r" fill="none" stroke="#eef2f7" stroke-width="8" />
                              <circle
                                v-for="seg in r.subChart.segments"
                                :key="seg.label"
                                cx="35" cy="35" :r="r.subChart.r"
                                fill="none"
                                :stroke="seg.color"
                                stroke-width="8"
                                stroke-linecap="round"
                                :stroke-dasharray="seg.dash"
                                :stroke-dashoffset="seg.offset"
                                transform="rotate(-90 35 35)"
                              />
                              <text x="35" y="34" text-anchor="middle" class="subpanel-donut-num">{{ r.subStats.pct }}%</text>
                              <text x="35" y="45" text-anchor="middle" class="subpanel-donut-lbl">bajarildi</text>
                            </svg>
                          </div>
                          <div class="subpanel-main">
                            <div class="subpanel-title">
                              <i class="fa-solid fa-diagram-project"></i> Subtasklar
                              <span class="subpanel-title-count">{{ r.subStats.done }}/{{ r.subStats.total }}</span>
                            </div>
                            <div class="subpanel-stats">
                              <span class="substat"><b>{{ r.subStats.total }}</b> jami</span>
                              <span class="substat substat--done"><i class="fa-solid fa-circle-check"></i> {{ r.subStats.done }}</span>
                              <span v-if="r.subStats.inProgress" class="substat substat--prog"><i class="fa-solid fa-rotate"></i> {{ r.subStats.inProgress }}</span>
                              <span v-if="r.subStats.fresh" class="substat substat--new"><i class="fa-regular fa-circle"></i> {{ r.subStats.fresh }}</span>
                              <span v-if="r.subStats.overdue" class="substat substat--over"><i class="fa-solid fa-triangle-exclamation"></i> {{ r.subStats.overdue }}</span>
                            </div>
                            <div class="subpanel-progress">
                              <div class="subpanel-progress-fill" :style="{ width: r.subStats.pct + '%' }"></div>
                            </div>
                          </div>
                        </div>

                        <!-- Subtask kartalari -->
                        <div class="subcards">
                          <div
                            v-for="s in r.subList"
                            :key="s.id"
                            class="subcard"
                            :class="s.done ? 'is-done' : (s.overdue ? 'is-over' : (s.statusLabel === 'Jarayonda' ? 'is-prog' : 'is-new'))"
                          >
                            <div class="subcard-top">
                              <i class="subcard-check" :class="s.done ? 'fa-solid fa-circle-check' : 'fa-regular fa-circle'"></i>
                              <span class="subcard-name" :title="s.name">{{ s.name }}</span>
                              <span class="subcard-badge">{{ s.statusLabel }}</span>
                            </div>
                            <div v-if="s.description" class="subcard-desc" :title="s.description">{{ s.description }}</div>
                            <div class="subcard-foot">
                              <div class="subcard-people">
                                <template v-if="s.ijro.length">
                                  <span
                                    v-for="a in s.ijro"
                                    :key="a.user_id"
                                    class="subcard-person"
                                    :title="a.fullName + ' — Ijrochi'"
                                  >
                                    <img :src="avatarUrl(a.avatar)" class="subcard-ava" @error="onImgErr" />
                                    {{ a.name }}
                                  </span>
                                </template>
                                <span v-else class="subcard-noexec"><i class="fa-solid fa-user-slash"></i> Ijrochi yo'q</span>
                                <span
                                  v-if="s.malumot.length"
                                  class="subcard-info-chip"
                                  :title="'Malumot uchun: ' + namesTitle(s.malumot)"
                                >
                                  <i class="fa-regular fa-eye"></i> {{ s.malumot.length }}
                                </span>
                              </div>
                              <span v-if="s.deadlineLabel" class="subcard-deadline" :class="{ over: s.overdue }">
                                <i class="fa-regular fa-clock"></i> {{ s.deadlineLabel }}
                                <span v-if="s.daysText" class="subcard-days">· {{ s.daysText }}</span>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                  </template>
                  <tr v-if="!tableRows.length">
                    <td colspan="9" class="td-empty-row">
                      <i class="fa-regular fa-folder-open" style="font-size:22px; display:block; margin-bottom:6px; color:#cbd5e1;"></i>
                      Natija topilmadi
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import api from "@/services/api";
import * as XLSX from "xlsx";

// Ish oqimi tartibi: Qabul qilindi — eng oxirgi (yakuniy) status
const STATUS_ORDER = ["Yangi", "Jarayonda", "Yakunlandi", "Tasdiqlandi", "Qabul qilindi"];
const STATUS_META = {
  "Yangi": "#3b82f6",
  "Jarayonda": "#f59e0b",
  "Yakunlandi": "#8b5cf6",
  "Tasdiqlandi": "#06b6d4",
  "Qabul qilindi": "#10b981",
};
// "Ish tugagan" deb hisoblanadigan statuslar (overdue'dan chiqariladi)
const DONE_WORK = new Set(["Yakunlandi", "Tasdiqlandi", "Qabul qilindi"]);
// Ijrochi uchun "bajarilgan" — muallif tasdiqlagan yoki qabul qilgan.
// "Yakunlandi" kirmaydi: ijrochi tugatgan, ammo muallif hali tasdiqlamagan.
const EXECUTOR_DONE = new Set(["Tasdiqlandi", "Qabul qilindi"]);
const FALLBACK_COLORS = ["#ec4899", "#14b8a6", "#f97316", "#a855f7"];
const DONE_SUBTASK = new Set(["done", "completed", "finished", "qabul qilindi", "tugatilgan", "bajarildi"]);

export default {
  name: "UserStatisticsModal",
  props: {
    modelValue: { type: Boolean, default: false },
    workspaceId: { type: [Number, String], default: null },
    workspaceName: { type: String, default: "" },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      loading: false,
      infoMessage: "",
      tasks: [],
      activeTab: "overview",
      tabs: [
        { key: "overview", label: "Umumiy", icon: "fa-solid fa-chart-pie" },
        // { key: "employees", label: "Xodimlar", icon: "fa-solid fa-users" },
        // { key: "deadlines", label: "Muddatlar", icon: "fa-solid fa-calendar-days" },
        { key: "table", label: "Jadval", icon: "fa-solid fa-table" },
      ],
      donutR: 74,
      selectedStatus: null,
      expandedTaskId: null,
      lineW: 700,
      lineH: 220,
      linePad: 20,
      // table state
      tblSearch: "",
      tblStatus: "",
      tblEmployee: "",
      tblRole: "all", // all | ijro | author
      tblOverdue: "all", // all | task | subtask — muddat o'tgan bo'yicha filter
      expandedTblIds: [], // jadvalda subtaski ochilgan qatorlar
      sortKey: "created",
      sortDir: "desc",
    };
  },
  computed: {
    statusList() {
      const present = new Set(this.tasks.map((t) => t.status || "Noma'lum"));
      const ordered = STATUS_ORDER.filter((s) => present.has(s));
      const extras = [...present].filter((s) => !STATUS_ORDER.includes(s));
      // Qabul qilindi doim oxirida qolishi uchun ekstralarni undan oldin joylashtiramiz
      const finalIdx = ordered.indexOf("Qabul qilindi");
      if (finalIdx !== -1) ordered.splice(finalIdx, 0, ...extras);
      else ordered.push(...extras);
      return ordered;
    },

    kpi() {
      const total = this.tasks.length;
      let completed = 0, inProgress = 0, fresh = 0, overdue = 0;
      let completionSum = 0, completionCount = 0;

      this.tasks.forEach((t) => {
        if (t.status === "Qabul qilindi") completed++;
        else if (t.status === "Jarayonda") inProgress++;
        else if (t.status === "Yangi") fresh++;

        if (this.isOverdue(t)) overdue++;

        const c = this.parseDate(t.created_at);
        const done = this.parseDate(t.completed_at);
        if (c && done) {
          completionSum += (done - c) / 86400000;
          completionCount++;
        }
      });

      return {
        total,
        completed,
        inProgress,
        fresh,
        overdue,
        completionPct: total ? Math.round((completed / total) * 100) : 0,
        avgCompletionDays: completionCount
          ? Math.round((completionSum / completionCount) * 10) / 10
          : 0,
      };
    },

    statusCounts() {
      const m = {};
      this.tasks.forEach((t) => {
        const s = t.status || "Noma'lum";
        m[s] = (m[s] || 0) + 1;
      });
      return m;
    },

    // user_id → {name, avatar}: barcha a'zolar va subtask ijrochilaridan yig'amiz
    // (project-stats muallif ismini bermaydi, shu yo'l bilan aniqlaymiz)
    userDirectory() {
      const dir = new Map();
      const put = (id, name, avatar) => {
        if (id === null || id === undefined) return;
        if (!dir.has(id)) dir.set(id, { name: name || null, avatar: avatar || null });
        else {
          const e = dir.get(id);
          if (!e.name && name) e.name = name;
          if (!e.avatar && avatar) e.avatar = avatar;
        }
      };
      this.tasks.forEach((t) => {
        (t.task_members || []).forEach((m) => put(m.user_id, m.full_name, m.avatar));
        (t.subtasks || []).forEach((st) =>
          (st.assignees || []).forEach((a) =>
            put(a.user_id, `${a.firstname || ""} ${a.lastname || ""}`.trim(), a.avatar)
          )
        );
      });
      return dir;
    },

    donutSegments() {
      const total = this.tasks.length || 1;
      const C = 2 * Math.PI * this.donutR;
      let cumulative = 0;
      // statusList tartibida (Qabul qilindi oxirida)
      return this.statusList
        .filter((label) => this.statusCounts[label])
        .map((label) => {
          const value = this.statusCounts[label];
          const frac = value / total;
          const seg = {
            label,
            value,
            pct: Math.round(frac * 100),
            color: this.statusColor(label),
            dash: `${frac * C} ${C}`,
            offset: -cumulative * C,
          };
          cumulative += frac;
          return seg;
        });
    },

    // Donutda tanlangan status bo'yicha topshiriqlar (kim ijroda, muddat, izoh, subtask)
    drilldownTasks() {
      if (!this.selectedStatus) return [];
      return this.tasks
        .filter((t) => (t.status || "Noma'lum") === this.selectedStatus)
        .map((t) => {
          const members = t.task_members || [];
          const ijroMembers = members.filter((m) => m.process === "Ijro");
          const infoCount = members.filter((m) => m.process === "Malumot").length;

          const deadline = this.parseDate(t.task_deadline);
          const created = this.parseDate(t.created_at);
          const daysLeft = deadline
            ? Math.ceil((this.startOf(deadline) - this.startOfToday()) / 86400000)
            : null;

          const subs = t.subtasks || [];
          const subsDone = subs.filter((s) => this.isSubtaskDone(s)).length;

          const creator = this.userDirectory.get(t.user_id);

          return {
            id: t.id,
            name: t.task_name,
            overdue: this.isOverdue(t),
            deadlineLabel: deadline ? this.fmtDate(deadline) : "—",
            createdLabel: created ? this.fmtDate(created) : "—",
            daysLabel: this.daysLabel(daysLeft, t.status),
            daysTone: this.drilldownDaysTone(t, daysLeft),
            ijroMembers,
            // Ro'yxatda qisqa (M.Tolibov), tooltipda to'liq ism
            ijroNames: ijroMembers.map((m) => this.shortName(m.full_name)).filter(Boolean).join(", "),
            ijroNamesFull: ijroMembers.map((m) => m.full_name).filter(Boolean).join(", "),
            infoCount,
            creatorName: this.shortName(creator?.name || (t.user_id ? `ID: ${t.user_id}` : "")),
            creatorNameFull: creator?.name || (t.user_id ? `ID: ${t.user_id}` : "—"),
            comments: t.comments_count || 0,
            subsTotal: subs.length,
            subsDone,
            subsPct: subs.length ? Math.round((subsDone / subs.length) * 100) : 0,
            hasSubs: subs.length > 0,
            // Subtask chart uchun batafsil
            subChart: this.buildSubtaskChart(subs),
            subList: subs.map((s) => ({
              id: s.id,
              name: s.name,
              done: this.isSubtaskDone(s),
              statusLabel: this.subtaskStatusLabel(s),
              deadlineLabel: this.parseDate(s.deadline) ? this.fmtDate(this.parseDate(s.deadline)) : null,
              assignees: (s.assignees || []).map((a) => ({
                user_id: a.user_id,
                name: this.shortName(`${a.firstname || ""} ${a.lastname || ""}`.trim()),
                fullName: `${a.firstname || ""} ${a.lastname || ""}`.trim(),
                avatar: a.avatar,
                process: a.process,
              })),
            })),
            isChecked: !!t.is_checked,
          };
        })
        .sort((a, b) => Number(b.overdue) - Number(a.overdue));
    },

    statusDeadline() {
      const rows = this.statusList
        .map((status) => {
          const list = this.tasks.filter((t) => (t.status || "Noma'lum") === status);
          const overdue = list.filter((t) => this.isOverdue(t)).length;
          const total = list.length;
          return { status, color: this.statusColor(status), total, overdue, onTime: total - overdue };
        })
        .filter((s) => s.total > 0);

      const max = Math.max(...rows.map((r) => r.total), 1);
      return rows.map((r) => ({
        ...r,
        // butun bar uzunligi eng ko'p statusga nisbatan (task soniga proporsional)
        widthPct: (r.total / max) * 100,
        // ichkarida muddatida / kechikkan nisbati
        onTimePct: r.total ? (r.onTime / r.total) * 100 : 0,
        overduePct: r.total ? (r.overdue / r.total) * 100 : 0,
      }));
    },

    // Ijrochilar samaradorligi: biriktirilgan vs bajarilgan
    // "Bajarilgan" = ijrochi tugatgan va muallif tasdiqlagan (Tasdiqlandi yoki Qabul qilindi)
    employeeEfficiency() {
      const m = new Map();
      this.tasks.forEach((t) => {
        const done = EXECUTOR_DONE.has(t.status);
        const c = this.parseDate(t.created_at);
        const fin = this.parseDate(t.completed_at);
        const cycle = c && fin ? (fin - c) / 86400000 : null;

        const ensure = (id, name, avatar) => {
          let e = m.get(id);
          if (!e) {
            e = {
              user_id: id,
              name: name || `ID: ${id}`,
              avatar: avatar || null,
              assigned: 0,
              done: 0,
              overdue: 0,
              subAssigned: 0,
              cycleSum: 0,
              cycleCount: 0,
            };
            m.set(id, e);
          } else if (!e.avatar && avatar) e.avatar = avatar;
          return e;
        };

        (t.task_members || [])
          .filter((mem) => mem.process === "Ijro")
          .forEach((mem) => {
            const e = ensure(mem.user_id, mem.full_name, mem.avatar);
            e.assigned++;
            if (done) e.done++;
            if (this.isOverdue(t)) e.overdue++;
            if (cycle !== null) {
              e.cycleSum += cycle;
              e.cycleCount++;
            }
          });

        // Subtask ijrochilari — task bilan bir xil darajada hisoblanadi:
        // har bir subtask "assigned" ga, bajarilgani "done" ga kiradi (foizga ta'sir qiladi)
        (t.subtasks || []).forEach((st) => {
          const stDone = this.isSubtaskDone(st);
          const stDeadline = this.parseDate(st.deadline);
          const stOverdue =
            !stDone && stDeadline && this.startOf(stDeadline) < this.startOfToday();
          (st.assignees || [])
            .filter((a) => a.process === "Ijro")
            .forEach((a) => {
              const name = `${a.firstname || ""} ${a.lastname || ""}`.trim();
              const e = ensure(a.user_id, name, a.avatar);
              e.assigned++; // subtask ham topshiriq kabi hisobga kiradi
              e.subAssigned++; // nechtasi subtask ekanini ko'rsatish uchun
              if (stDone) e.done++;
              if (stOverdue) e.overdue++;
            });
        });
      });
      return [...m.values()]
        .map((e) => ({
          ...e,
          rate: e.assigned ? Math.round((e.done / e.assigned) * 100) : 0,
          avgCycle: e.cycleCount ? Math.round((e.cycleSum / e.cycleCount) * 10) / 10 : null,
        }))
        .sort((a, b) => b.rate - a.rate || b.done - a.done);
    },

    // Podium uchun top-3 ijrochi (samaradorlik + bajarilgan hisobiga)
    topExecutors() {
      const ranked = [...this.employeeEfficiency].sort(
        (a, b) => b.done - a.done || b.rate - a.rate
      );
      const top = ranked.slice(0, 3);
      // Podium tartibi: 2-o'rin | 1-o'rin | 3-o'rin
      const order = [1, 0, 2];
      return order
        .map((i) => (top[i] ? { ...top[i], rank: i + 1 } : null))
        .filter(Boolean);
    },

    // Subtasklari bor topshiriqlar progressi + umumiy statistika
    subtasksData() {
      const withSubs = this.tasks
        .filter((t) => (t.subtasks || []).length > 0)
        .map((t) => {
          const subs = t.subtasks || [];
          const done = subs.filter((s) =>
            DONE_SUBTASK.has(String(s.status || "").toLowerCase())
          ).length;
          return {
            id: t.id,
            name: t.task_name,
            status: t.status,
            total: subs.length,
            done,
            pct: subs.length ? Math.round((done / subs.length) * 100) : 0,
          };
        })
        .sort((a, b) => b.total - a.total);

      const totalSubs = withSubs.reduce((s, t) => s + t.total, 0);
      const doneSubs = withSubs.reduce((s, t) => s + t.done, 0);
      return {
        tasks: withSubs,
        tasksWithSubs: withSubs.length,
        totalSubs,
        doneSubs,
        pct: totalSubs ? Math.round((doneSubs / totalSubs) * 100) : 0,
      };
    },

    workload() {
      const m = new Map();
      const ensure = (id, name, avatar) => {
        let e = m.get(id);
        if (!e) {
          e = {
            user_id: id,
            name: name || `ID: ${id}`,
            avatar: avatar || null,
            ijro: 0,
            malumot: 0,
            other: 0,
            subIjro: 0,     // subtask ichidagi Ijro
            subMalumot: 0,  // subtask ichidagi Malumot
          };
          m.set(id, e);
        } else if (!e.avatar && avatar) e.avatar = avatar;
        return e;
      };

      this.tasks.forEach((t) => {
        // Asosiy task a'zolari
        (t.task_members || []).forEach((mem) => {
          const e = ensure(mem.user_id, mem.full_name, mem.avatar);
          if (mem.process === "Ijro") e.ijro++;
          else if (mem.process === "Malumot") e.malumot++;
          else e.other++;
        });
        // Subtask ijrochilari — alohida hisoblanadi
        (t.subtasks || []).forEach((st) => {
          (st.assignees || []).forEach((a) => {
            const name = `${a.firstname || ""} ${a.lastname || ""}`.trim();
            const e = ensure(a.user_id, name, a.avatar);
            if (a.process === "Malumot") e.subMalumot++;
            else e.subIjro++;
          });
        });
      });

      return [...m.values()]
        .map((e) => {
          const taskTotal = e.ijro + e.malumot + e.other;
          const subTotal = e.subIjro + e.subMalumot;
          return {
            ...e,
            taskTotal,
            subTotal,
            total: taskTotal + subTotal,
            // ortiqcha yuk: task + subtask ijrolari birga
            overloaded: e.ijro + e.subIjro >= 3,
          };
        })
        .filter((e) => e.total > 0)
        .sort((a, b) => b.ijro + b.subIjro - (a.ijro + a.subIjro) || b.total - a.total);
    },

    workloadMax() {
      return Math.max(
        ...this.workload.map((w) => w.ijro + w.malumot + w.subIjro + w.subMalumot),
        1
      );
    },

    employeeStatusMatrix() {
      const m = new Map();
      this.tasks.forEach((t) => {
        (t.task_members || [])
          .filter((mem) => mem.process === "Ijro")
          .forEach((mem) => {
            let e = m.get(mem.user_id);
            if (!e) {
              e = { user_id: mem.user_id, name: mem.full_name, avatar: mem.avatar, counts: {}, total: 0 };
              m.set(mem.user_id, e);
            }
            const s = t.status || "Noma'lum";
            e.counts[s] = (e.counts[s] || 0) + 1;
            e.total++;
          });
      });
      return [...m.values()].sort((a, b) => b.total - a.total);
    },

    // Har status ustuni uchun alohida maksimal — rang intensivligini shu ustunga nisbatan hisoblaymiz
    matrixColMax() {
      const m = {};
      this.statusList.forEach((s) => {
        m[s] = Math.max(
          ...this.employeeStatusMatrix.map((r) => r.counts[s] || 0),
          1
        );
      });
      return m;
    },

    timeline() {
      const buckets = new Map();
      const add = (dateStr, key) => {
        const d = this.parseDate(dateStr);
        if (!d) return;
        const k = d.toISOString().slice(0, 10);
        if (!buckets.has(k)) buckets.set(k, { key: k, created: 0, completed: 0 });
        buckets.get(k)[key]++;
      };
      this.tasks.forEach((t) => {
        add(t.created_at, "created");
        add(t.completed_at, "completed");
      });

      const sorted = [...buckets.values()].sort((a, b) => a.key.localeCompare(b.key));
      const list = sorted.map((b) => ({
        ...b,
        short: b.key.slice(5).replace("-", "/"),
      }));

      const maxVal = Math.max(...list.map((b) => Math.max(b.created, b.completed)), 1);
      const n = list.length;
      const usableW = this.lineW;
      const usableH = this.lineH - this.linePad * 2;

      const x = (i) => (n === 1 ? usableW / 2 : (i / (n - 1)) * usableW);
      const yFor = (v) => this.linePad + usableH - (v / maxVal) * usableH;

      return {
        buckets: list,
        x,
        yCreated: (i) => yFor(list[i].created),
        yCompleted: (i) => yFor(list[i].completed),
        createdPoints: list.map((b, i) => `${x(i)},${yFor(b.created)}`).join(" "),
        completedPoints: list.map((b, i) => `${x(i)},${yFor(b.completed)}`).join(" "),
      };
    },

    gantt() {
      const rows = this.tasks
        .map((t) => {
          const start = this.parseDate(t.created_at);
          const end = this.parseDate(t.task_deadline) || start;
          return start && end ? { t, start, end } : null;
        })
        .filter(Boolean);

      if (!rows.length) return { rows: [], months: [], todayPos: 0 };

      const today = this.startOfToday();
      let min = Math.min(...rows.map((r) => r.start.getTime()));
      let max = Math.max(...rows.map((r) => r.end.getTime()), today.getTime());
      const range = max - min || 1;

      const mapped = rows
        .sort((a, b) => a.start - b.start)
        .map(({ t, start, end }) => {
          const overdue = this.isOverdue(t);
          const left = ((start.getTime() - min) / range) * 100;
          const width = Math.max(((end.getTime() - start.getTime()) / range) * 100, 1.5);
          const startLabel = this.fmtDate(start);
          const endLabel = this.fmtDate(end);
          return {
            id: t.id,
            name: t.task_name,
            left,
            width,
            overdue,
            color: overdue ? "#ef4444" : this.statusColor(t.status),
            startLabel,
            endLabel,
            tooltip:
              `${t.task_name}\n${startLabel} → ${endLabel}` +
              (overdue ? " (muddati o'tgan)" : ""),
          };
        });

      // Month markers
      const months = [];
      const cur = new Date(min);
      cur.setDate(1);
      while (cur.getTime() <= max) {
        const pos = ((cur.getTime() - min) / range) * 100;
        if (pos >= 0 && pos <= 100) {
          months.push({
            pos,
            label: cur.toLocaleDateString("uz-UZ", { month: "short", year: "2-digit" }),
          });
        }
        cur.setMonth(cur.getMonth() + 1);
      }

      return {
        rows: mapped,
        months,
        todayPos: ((today.getTime() - min) / range) * 100,
      };
    },

    // Filtr uchun xodimlar — a'zolar ham, mualliflar ham
    employeeOptions() {
      const m = new Map();
      this.tasks.forEach((t) => {
        (t.task_members || []).forEach((mem) => {
          if (!m.has(mem.user_id)) m.set(mem.user_id, { user_id: mem.user_id, name: mem.full_name });
        });
        if (t.user_id != null && !m.has(t.user_id)) {
          const c = this.userDirectory.get(t.user_id);
          m.set(t.user_id, { user_id: t.user_id, name: c?.name || `ID: ${t.user_id}` });
        }
      });
      return [...m.values()]
        .map((e) => ({ ...e, name: e.name || `ID: ${e.user_id}` }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },

    tableRows() {
      const q = this.tblSearch.trim().toLowerCase();
      let rows = this.tasks.map((t) => {
        const ijroMembers = (t.task_members || []).filter((m) => m.process === "Ijro");
        const subtasks = t.subtasks || [];
        const subtaskDone = subtasks.filter((s) =>
          DONE_SUBTASK.has(String(s.status || "").toLowerCase())
        ).length;
        const deadline = this.parseDate(t.task_deadline);
        const daysLeft = deadline
          ? Math.ceil((this.startOf(deadline) - this.startOfToday()) / 86400000)
          : null;
        const creator = this.userDirectory.get(t.user_id);
        const ijroShort = ijroMembers.map((m) => this.shortName(m.full_name)).filter(Boolean);

        // Subtasklar bo'yicha boyitilgan ma'lumot
        const subList = subtasks.map((s) => {
          const sd = this.parseDate(s.deadline);
          const sDaysLeft = sd
            ? Math.ceil((this.startOf(sd) - this.startOfToday()) / 86400000)
            : null;
          const done = this.isSubtaskDone(s);
          const sOverdue = !done && sDaysLeft !== null && sDaysLeft < 0;
          const allAssignees = (s.assignees || []).map((a) => ({
            user_id: a.user_id,
            name: this.shortName(`${a.firstname || ""} ${a.lastname || ""}`.trim()),
            fullName: `${a.firstname || ""} ${a.lastname || ""}`.trim(),
            avatar: a.avatar,
            process: a.process,
          }));
          return {
            id: s.id,
            name: s.name,
            description: s.description || s.text || "",
            done,
            statusLabel: this.subtaskStatusLabel(s),
            deadlineLabel: sd ? this.fmtDate(sd) : null,
            daysLeft: sDaysLeft,
            overdue: sOverdue,
            daysText:
              done || sDaysLeft === null
                ? ""
                : sDaysLeft < 0
                ? `${Math.abs(sDaysLeft)} kun kechikdi`
                : sDaysLeft === 0
                ? "Bugun"
                : `${sDaysLeft} kun qoldi`,
            ijro: allAssignees.filter((a) => a.process === "Ijro"),
            malumot: allAssignees.filter((a) => a.process === "Malumot"),
            assignees: allAssignees,
          };
        });
        const subInProgress = subList.filter(
          (s) => !s.done && s.statusLabel === "Jarayonda"
        ).length;
        const subFresh = subList.filter(
          (s) => !s.done && s.statusLabel !== "Jarayonda"
        ).length;
        const subOverdue = subList.filter((s) => s.overdue).length;
        const subStats = {
          total: subtasks.length,
          done: subtaskDone,
          inProgress: subInProgress,
          fresh: subFresh,
          overdue: subOverdue,
          pct: subtasks.length ? Math.round((subtaskDone / subtasks.length) * 100) : 0,
        };

        return {
          id: t.id,
          taskNumber: t.task_number || `#${t.id}`,
          task_name: t.task_name,
          description: t.description || t.task_description || "",
          status: t.status,
          created: this.parseDate(t.created_at),
          createdLabel: t.created_at ? this.fmtDate(this.parseDate(t.created_at)) : "—",
          deadline,
          deadlineLabel: deadline ? this.fmtDate(deadline) : "—",
          completedLabel: t.completed_at ? this.fmtDate(this.parseDate(t.completed_at)) : (DONE_WORK && DONE_WORK.has(t.status) ? "Tasdiqlangan" : "—"),
          daysLeft: daysLeft === null ? Infinity : daysLeft,
          daysLabel: this.daysLabel(daysLeft, t.status),
          overdue: this.isOverdue(t),
          ijroMembers,
          ijroIds: ijroMembers.map((m) => m.user_id),
          // Ijrochi ismlari: ro'yxatda birinchi qisqa ism + "+N", tooltipda to'liq
          ijroLabel: ijroShort.length
            ? ijroShort[0] + (ijroShort.length > 1 ? ` +${ijroShort.length - 1}` : "")
            : "",
          ijroFull: ijroMembers.map((m) => m.full_name).filter(Boolean).join(", "),
          memberIds: (t.task_members || []).map((m) => m.user_id),
          authorId: t.user_id,
          authorName: this.shortName(creator?.name || (t.user_id ? `ID: ${t.user_id}` : "—")),
          authorFull: creator?.name || (t.user_id ? `ID: ${t.user_id}` : "—"),
          authorAvatar: creator?.avatar,
          subtaskTotal: subtasks.length,
          subtaskDone,
          subtaskPct: subtasks.length ? Math.round((subtaskDone / subtasks.length) * 100) : 0,
          subStats,
          subChart: this.buildSubtaskChart(subtasks),
          subList,
        };
      });

      if (q) rows = rows.filter((r) => (r.task_name || "").toLowerCase().includes(q));
      if (this.tblStatus) rows = rows.filter((r) => r.status === this.tblStatus);

      if (this.tblEmployee) {
        const uid = Number(this.tblEmployee);
        rows = rows.filter((r) => {
          const isIjro = r.ijroIds.includes(uid);
          const isAuthor = r.authorId === uid;
          if (this.tblRole === "ijro") return isIjro;
          if (this.tblRole === "author") return isAuthor;
          return isIjro || isAuthor || r.memberIds.includes(uid);
        });
      }

      // Muddat o'tgan bo'yicha filter: task (topshiriqning o'z muddati) yoki subtask
      if (this.tblOverdue === "task") {
        rows = rows.filter((r) => r.overdue);
      } else if (this.tblOverdue === "subtask") {
        rows = rows.filter((r) => r.subStats.overdue > 0);
      }

      const dir = this.sortDir === "asc" ? 1 : -1;
      const key = this.sortKey;
      rows.sort((a, b) => {
        let av = a[key], bv = b[key];
        if (key === "deadline" || key === "created") {
          av = a[key] ? a[key].getTime() : 0;
          bv = b[key] ? b[key].getTime() : 0;
        }
        if (typeof av === "string") return av.localeCompare(bv) * dir;
        return (av - bv) * dir;
      });
      return rows;
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.fetchStats();
    },
    workspaceId() {
      if (this.modelValue) this.fetchStats();
    },
    tblEmployee(val) {
      // Xodim tozalansa rol filtri "Ikkalasi"ga qaytadi
      if (!val) this.tblRole = "all";
    },
  },
  methods: {
    exportTableToExcel() {
      if (!this.tableRows || this.tableRows.length === 0) {
        Swal.fire({
          icon: 'info',
          title: 'Ma\'lumot yo\'q',
          text: 'Eksport qilish uchun jadvalda ma\'lumot yo\'q',
        });
        return;
      }

      // 1) Find max number of subtasks
      let maxSubtasks = 0;
      this.tableRows.forEach(row => {
        if (row.subList && row.subList.length > maxSubtasks) {
          maxSubtasks = row.subList.length;
        }
      });

      // 2) Prepare headers
      const headers = [
        "Tartib",
        "Topshiriq №",
        "Topshiriq nomi",
        "Tavsif",
        "Status",
        "Ijrochi",
        "Muallif",
        "Yaratilgan sana",
        "Muddat",
        "Tasdiqlangan sana",
        "Qolgan (kun)",
        "Subtasklar soni"
      ];

      for (let i = 1; i <= maxSubtasks; i++) {
        headers.push(`Subtask ${i} Nomi`);
        headers.push(`Subtask ${i} Status`);
        headers.push(`Subtask ${i} Muddat`);
        headers.push(`Subtask ${i} Ijrochi`);
      }

      const excelData = [headers];

      // 3) Prepare rows
      this.tableRows.forEach((row, index) => {
        const r = [
          index + 1,
          row.taskNumber || '',
          row.task_name || '',
          row.description || '',
          row.status || '',
          row.ijroFull || '',
          row.authorFull || '',
          row.createdLabel || '',
          row.deadlineLabel || '',
          row.completedLabel || '',
          row.daysLabel || '',
          row.subtaskTotal || 0
        ];

        for (let i = 0; i < maxSubtasks; i++) {
          if (row.subList && row.subList[i]) {
            const sub = row.subList[i];
            r.push(sub.name || '');
            r.push(sub.statusLabel || '');
            r.push(sub.deadlineLabel || '');
            const subIjroStr = (sub.assignees || []).map(a => a.fullName).join(', ');
            r.push(subIjroStr);
          } else {
            r.push('');
            r.push('');
            r.push('');
            r.push('');
          }
        }

        excelData.push(r);
      });

      // 4) Download as XLSX
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Tasklar");

      const dateStr = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `Tasklar_${dateStr}.xlsx`);
    },

    fetchStats() {
      this.loading = true;
      this.infoMessage = "";
      this.selectedStatus = null;
      this.expandedTaskId = null;
      api
        .get("/project-stats", { params: { workspace_id: this.workspaceId } })
        .then((res) => {
          if (Array.isArray(res.data)) {
            this.tasks = res.data;
          } else {
            this.tasks = [];
            this.infoMessage = res.data?.message || "";
          }
        })
        .catch((error) => {
          console.error("❌ project-stats:", error.response?.data || error.message);
          this.tasks = [];
          this.infoMessage =
            error.response?.data?.message || "Statistikani yuklashda xatolik yuz berdi";
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // ---- helpers ----
    parseDate(str) {
      if (!str) return null;
      const d = new Date(str);
      return isNaN(d.getTime()) ? null : d;
    },
    startOf(d) {
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      return x;
    },
    startOfToday() {
      return this.startOf(new Date());
    },
    isOverdue(t) {
      const d = this.parseDate(t.task_deadline);
      if (!d) return false;
      // Ishi tugagan (Yakunlandi/Tasdiqlandi/Qabul qilindi) topshiriq kechikkan hisoblanmaydi
      if (DONE_WORK.has(t.status)) return false;
      return this.startOf(d) < this.startOfToday();
    },
    fmtDate(d) {
      return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" });
    },
    daysLabel(days, status) {
      if (days === null || days === undefined) return "—";
      if (status === "Qabul qilindi") return "Bajarildi";
      if (days < 0) return `${Math.abs(days)} kun kech`;
      if (days === 0) return "Bugun";
      return `${days} kun`;
    },
    daysClass(r) {
      if (r.status === "Qabul qilindi") return "days-done";
      if (r.overdue) return "days-over";
      if (r.daysLeft === 0) return "days-today";
      return "days-ok";
    },
    statusColor(status) {
      if (STATUS_META[status]) return STATUS_META[status];
      // noma'lum statuslarga barqaror rang beramiz
      const extras = this.statusList.filter((s) => !STATUS_META[s]);
      const idx = extras.indexOf(status);
      return FALLBACK_COLORS[(idx < 0 ? 0 : idx) % FALLBACK_COLORS.length];
    },
    // Katak o'z statusi rangida — intensivlik ustundagi maksimalga nisbatan
    cellStyle(value, status) {
      if (!value) return { background: "#f8fafc", color: "#cbd5e1" };
      const max = this.matrixColMax[status] || 1;
      const intensity = 0.15 + (value / max) * 0.6;
      const rgb = this.hexToRgb(this.statusColor(status));
      return {
        background: `rgba(${rgb}, ${intensity})`,
        color: intensity > 0.5 ? "#fff" : "#1e293b",
        fontWeight: 700,
      };
    },
    hexToRgb(hex) {
      const h = hex.replace("#", "");
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      return `${r}, ${g}, ${b}`;
    },
    selectStatus(label) {
      this.selectedStatus = this.selectedStatus === label ? null : label;
      this.expandedTaskId = null;
    },
    isTblExpanded(id) {
      return this.expandedTblIds.includes(id);
    },
    toggleTblRow(id) {
      const i = this.expandedTblIds.indexOf(id);
      if (i === -1) this.expandedTblIds.push(id);
      else this.expandedTblIds.splice(i, 1);
    },
    namesTitle(list) {
      return (list || []).map((a) => a.fullName || a.name).filter(Boolean).join(", ") || "—";
    },
    isSubtaskDone(s) {
      return DONE_SUBTASK.has(String(s.status || "").toLowerCase());
    },
    subtaskStatusLabel(s) {
      const raw = String(s.status || "").toLowerCase();
      if (this.isSubtaskDone(s)) return "Bajarilgan";
      if (raw === "new" || !raw) return "Yangi";
      if (raw.includes("progress") || raw.includes("jarayon")) return "Jarayonda";
      return s.status;
    },
    // Subtasklar bo'yicha mini-donut segmentlari (bajarilgan/jarayonda/yangi)
    buildSubtaskChart(subs) {
      const groups = {};
      subs.forEach((s) => {
        const label = this.subtaskStatusLabel(s);
        groups[label] = (groups[label] || 0) + 1;
      });
      const palette = {
        "Bajarilgan": "#10b981",
        "Jarayonda": "#f59e0b",
        "Yangi": "#3b82f6",
      };
      const total = subs.length || 1;
      const R = 26;
      const C = 2 * Math.PI * R;
      let cumulative = 0;
      const segments = Object.entries(groups).map(([label, value], i) => {
        const frac = value / total;
        const seg = {
          label,
          value,
          pct: Math.round(frac * 100),
          color: palette[label] || FALLBACK_COLORS[i % FALLBACK_COLORS.length],
          dash: `${frac * C} ${C}`,
          offset: -cumulative * C,
        };
        cumulative += frac;
        return seg;
      });
      return { segments, r: R, total: subs.length };
    },
    toggleDrilldownTask(id) {
      this.expandedTaskId = this.expandedTaskId === id ? null : id;
    },

    // "Mirolim Tolibov" → "M.Tolibov"
    shortName(fullName) {
      const s = String(fullName || "").trim();
      if (!s || s.startsWith("ID:")) return s || "—";
      const parts = s.split(/\s+/);
      if (parts.length === 1) return parts[0];
      return `${parts[0].charAt(0).toUpperCase()}.${parts.slice(1).join(" ")}`;
    },
    drilldownDaysTone(task, daysLeft) {
      if (DONE_WORK.has(task.status)) return "done";
      if (daysLeft === null) return "muted";
      if (daysLeft < 0) return "over";
      if (daysLeft === 0) return "today";
      return "ok";
    },
    effColor(rate) {
      if (rate >= 70) return "#10b981";
      if (rate >= 40) return "#f59e0b";
      return "#ef4444";
    },
    avatarUrl(avatar) {
      return `https://api.task-board.uz/storage/user/avatar/${avatar || "user.png"}`;
    },
    onImgErr(e) {
      e.target.src = "https://api.task-board.uz/storage/user/avatar/user.png";
    },
    sortBy(key) {
      if (this.sortKey === key) this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
      else {
        this.sortKey = key;
        this.sortDir = "asc";
      }
    },
    sortIcon(key) {
      if (this.sortKey !== key) return "fa-solid fa-sort sort-idle";
      return this.sortDir === "asc" ? "fa-solid fa-sort-up" : "fa-solid fa-sort-down";
    },
  },
};
</script>

<style scoped>
.dash {
  background: #eef1f7;
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  /* Poravlik meros bo'lib tarqaladi; ikonlar (FA/Material) o'z font-family'sini saqlaydi */
  font-family: "Poppins", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #0f172a;
  letter-spacing: -0.01em;
}

/* HEADER */
.dash-header {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 30px;
  background: #fff;
  border-bottom: 1px solid #e6e9f0;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.04);
}
.dash-header-icon {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 19px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  box-shadow: 0 8px 20px rgba(99, 102, 241, 0.32);
}
.dash-title {
  margin: 0;
  font-size: 21px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.02em;
}
.dash-subtitle {
  margin: 3px 0 0;
  font-size: 12.5px;
  font-weight: 400;
  color: #64748b;
}
.dash-header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.dash-ghost-btn,
.dash-close {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 11px;
  background: #f1f5f9;
  color: #64748b;
  transition: 0.2s;
}
.dash-ghost-btn:hover,
.dash-close:hover {
  background: #e2e8f0;
  color: #0f172a;
}

/* STATE */
.dash-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  color: #94a3b8;
  font-size: 14px;
}
.dash-state i {
  font-size: 42px;
  color: #cbd5e1;
}

.dash-body {
  padding: 22px 28px 60px;
  max-width: 1500px;
  width: 100%;
  margin: 0 auto;
}

/* KPI */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 14px;
  margin-bottom: 22px;
}
.kpi-card {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fff;
  border: 1px solid #e6e9f0;
  border-radius: 16px;
  padding: 18px 20px;
  position: relative;
  overflow: hidden;
  transition: 0.22s ease;
}
.kpi-card::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #6366f1;
  opacity: 0.85;
}
.kpi-card:hover {
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.08);
  transform: translateY(-2px);
  border-color: #dbe0ea;
}
.kpi-card.kpi-danger {
  border-color: #fecaca;
  background: linear-gradient(180deg, #fff5f5, #fff);
}
.kpi-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}
.kpi-meta {
  min-width: 0;
  flex: 1;
}
.kpi-value {
  font-size: 26px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.05;
  letter-spacing: -0.03em;
  display: flex;
  align-items: baseline;
  gap: 6px;
}
.kpi-value small {
  font-size: 13px;
  font-weight: 600;
  color: #94a3b8;
}
.kpi-sub {
  font-size: 12px;
  font-weight: 700;
  color: #10b981;
}
.kpi-label {
  font-size: 12px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-top: 2px;
}
.kpi-progress {
  margin-top: 8px;
  height: 5px;
  background: #eef2f7;
  border-radius: 3px;
  overflow: hidden;
}
.kpi-progress-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.5s ease;
}

/* TABS */
.dash-tabs {
  display: flex;
  gap: 4px;
  background: #fff;
  padding: 6px;
  border-radius: 14px;
  border: 1px solid #e6e9f0;
  margin-bottom: 22px;
  overflow-x: auto;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.03);
}
.dash-tab {
  flex: 1;
  min-width: 120px;
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
  padding: 11px 16px;
  border-radius: 10px;
  transition: 0.2s;
  white-space: nowrap;
}
.dash-tab i {
  margin-right: 5px;
  opacity: 0.85;
}
.dash-tab:hover {
  background: #f4f6fb;
  color: #1e293b;
}
.dash-tab.active {
  background: #405189;
  color: #fff;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(64, 81, 137, 0.28);
}

/* PANELS */
.tab-pane {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

/* ============ PODIUM ============ */
.podium-panel {
  background: radial-gradient(circle at 20% 0%, #1e293b 0%, #0f172a 60%);
  border-radius: 18px;
  padding: 24px 26px 0;
  position: relative;
  overflow: hidden;
  box-shadow: 0 12px 34px rgba(15, 23, 42, 0.28);
}
.podium-panel::before {
  content: "";
  position: absolute;
  top: -60px;
  right: -40px;
  width: 220px;
  height: 220px;
  background: radial-gradient(circle, rgba(139, 92, 246, 0.35), transparent 70%);
  pointer-events: none;
}
.podium-head {
  position: relative;
  z-index: 1;
  margin-bottom: 8px;
}
.podium-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 9px;
  letter-spacing: -0.01em;
}
.podium-title i {
  color: #fbbf24;
}
.podium-sub {
  margin: 4px 0 0;
  font-size: 12.5px;
  color: #94a3b8;
}
.podium {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: end;
  gap: 16px;
  max-width: 720px;
  margin: 0 auto;
}
.podium-spot {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
}
.podium-badge {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
  background: #cbd5e1;
  margin-bottom: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}
.podium-avatar-ring {
  padding: 3px;
  border-radius: 50%;
  background: linear-gradient(135deg, #64748b, #94a3b8);
}
.podium-avatar {
  width: 62px;
  height: 62px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #0f172a;
  display: block;
}
.podium-name {
  margin-top: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  max-width: 100%;
}
.podium-done {
  font-size: 12px;
  color: #cbd5e1;
  margin-top: 2px;
}
.podium-done small {
  color: #64748b;
  font-size: 10.5px;
}
.podium-stats {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  margin: 6px 0 12px;
}
.podium-rate {
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.02em;
}
.podium-assigned {
  font-size: 10.5px;
  color: #64748b;
}
.podium-stand {
  width: 100%;
  border-radius: 10px 10px 0 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 12px;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.55);
  font-size: 22px;
}
.podium-place {
  opacity: 0.8;
}

/* 1-o'rin — oltin, eng baland */
.rank-1 .podium-badge {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  color: #422006;
}
.rank-1 .podium-avatar-ring {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  box-shadow: 0 0 22px rgba(251, 191, 36, 0.5);
}
.rank-1 .podium-avatar {
  width: 76px;
  height: 76px;
}
.rank-1 .podium-stand {
  height: 92px;
  background: linear-gradient(180deg, rgba(251, 191, 36, 0.28), rgba(251, 191, 36, 0.06));
  border-top: 2px solid rgba(251, 191, 36, 0.55);
}

/* 2-o'rin — kumush */
.rank-2 .podium-avatar-ring {
  background: linear-gradient(135deg, #e2e8f0, #94a3b8);
}
.rank-2 .podium-stand {
  height: 66px;
  background: linear-gradient(180deg, rgba(203, 213, 225, 0.22), rgba(203, 213, 225, 0.05));
  border-top: 2px solid rgba(203, 213, 225, 0.5);
}

/* 3-o'rin — bronza */
.rank-3 .podium-badge {
  background: linear-gradient(135deg, #d97706, #b45309);
  color: #fff;
}
.rank-3 .podium-avatar-ring {
  background: linear-gradient(135deg, #f59e0b, #b45309);
}
.rank-3 .podium-stand {
  height: 50px;
  background: linear-gradient(180deg, rgba(217, 119, 6, 0.22), rgba(217, 119, 6, 0.05));
  border-top: 2px solid rgba(217, 119, 6, 0.5);
}

@media (max-width: 560px) {
  .podium {
    gap: 8px;
  }
  .podium-avatar,
  .rank-1 .podium-avatar {
    width: 52px;
    height: 52px;
  }
  .podium-name {
    font-size: 11px;
  }
}
.grid-2 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 18px;
}
.panel {
  background: #fff;
  border: 1px solid #e6e9f0;
  border-radius: 16px;
  padding: 22px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.03);
}
.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
}
.panel-head h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  letter-spacing: -0.01em;
}
.panel-hint {
  font-size: 12px;
  color: #94a3b8;
}
.panel-empty {
  text-align: center;
  padding: 40px 0;
  color: #cbd5e1;
  font-size: 13px;
}

/* DONUT */
.donut-wrap {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}
.donut-svg {
  width: 200px;
  height: 200px;
  flex-shrink: 0;
}
.donut-seg {
  transition: stroke-dasharray 0.5s ease;
}
.donut-center-num {
  font-size: 34px;
  font-weight: 800;
  fill: #1e293b;
}
.donut-center-label {
  font-size: 12px;
  fill: #94a3b8;
}
.donut-legend {
  flex: 1;
  min-width: 160px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}
.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 4px;
  flex-shrink: 0;
}
.legend-label {
  color: #475569;
  font-weight: 600;
}
.legend-value {
  margin-left: auto;
  font-weight: 700;
  color: #1e293b;
}
.legend-value small {
  color: #94a3b8;
  font-weight: 500;
}

/* LINE CHART */
.line-legend {
  display: flex;
  gap: 14px;
  font-size: 12px;
  color: #64748b;
  flex-wrap: wrap;
}
.line-legend .dot,
.overload-hint .dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  margin-right: 4px;
}
.line-chart {
  width: 100%;
}
.line-svg {
  width: 100%;
  height: 220px;
}
.line-x-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
  font-size: 10px;
  color: #94a3b8;
  overflow: hidden;
}
.line-x-labels span {
  flex-shrink: 0;
}

/* STATUS x DEADLINE */
.sxd-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.sxd-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.sxd-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  width: 130px;
  flex-shrink: 0;
}
.sxd-track {
  flex: 1;
  height: 22px;
  border-radius: 6px;
  background: #f1f5f9;
}
.sxd-bar {
  height: 100%;
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  min-width: 6px;
  transition: width 0.5s ease;
}
.sxd-ontime {
  /* rang inline style orqali — status o'z rangida */
  height: 100%;
}
.sxd-overdue {
  background: #ef4444;
  height: 100%;
  /* status rangidan ajralib turishi uchun chiziqli tekstura */
  background-image: repeating-linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.3),
    rgba(255, 255, 255, 0.3) 3px,
    transparent 3px,
    transparent 7px
  );
}
.sxd-nums {
  width: 90px;
  text-align: right;
  font-size: 13px;
  color: #1e293b;
  flex-shrink: 0;
}
.sxd-over-tag {
  display: inline-block;
  margin-left: 4px;
  font-size: 10px;
  font-weight: 700;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
  padding: 1px 5px;
  border-radius: 4px;
}

/* DONUT INTERACTIVITY */
.donut-seg {
  cursor: pointer;
  transition: opacity 0.25s ease, stroke-dasharray 0.5s ease;
}
.donut-seg:hover {
  opacity: 0.82;
}
.donut-seg--dim {
  opacity: 0.25;
}
.legend-clickable {
  cursor: pointer;
  padding: 5px 8px;
  border-radius: 8px;
  transition: 0.15s;
}
.legend-clickable:hover {
  background: #f4f6fb;
}
.legend-clickable.active {
  background: #eef2ff;
}

/* DRILLDOWN */
.drilldown {
  margin-top: 18px;
  border-top: 1px solid #eef2f7;
  padding-top: 14px;
}
.drilldown-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  color: #334155;
  margin-bottom: 10px;
}
.drilldown-head .legend-dot {
  margin-right: 4px;
}
.drilldown-close {
  border: none;
  background: #f1f5f9;
  color: #64748b;
  width: 26px;
  height: 26px;
  border-radius: 8px;
}
.drilldown-close:hover {
  background: #e2e8f0;
}
.drilldown-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 360px;
  overflow-y: auto;
  padding-right: 4px;
}
.drilldown-cell {
  flex-shrink: 0;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  overflow: hidden;
  transition: border-color 0.2s ease;
}
.drilldown-cell:hover {
  border-color: #c7d2fe;
}
.drilldown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  background: #f8fafc;
}
.drilldown-item.is-overdue {
  background: #fff5f5;
}
.drilldown-item.has-subs {
  cursor: pointer;
}
.drilldown-item.has-subs:hover {
  background: #f1f5ff;
}
.drilldown-item.is-expanded {
  background: #eef2ff;
}
/* Subtaskli topshiriq belgisi */
.dd-sub-marker {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  background: rgba(99, 102, 241, 0.14);
  color: #6366f1;
  font-size: 9px;
  margin-right: 5px;
  vertical-align: middle;
}
.dd-chip--sub {
  color: #6366f1;
  background: rgba(99, 102, 241, 0.1);
  font-weight: 600;
}
.dd-expand-icon {
  color: #94a3b8;
  font-size: 12px;
  margin-left: 4px;
  transition: transform 0.25s ease;
}
.dd-expand-icon.open {
  transform: rotate(180deg);
  color: #6366f1;
}

/* Ochiluvchi subtask chartlari */
.subchart {
  display: flex;
  gap: 18px;
  align-items: flex-start;
  padding: 14px;
  background: #fff;
  border-top: 1px dashed #dbe2ec;
  flex-wrap: wrap;
}
.subchart-donut {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.subchart-donut svg {
  width: 84px;
  height: 84px;
}
.subchart-num {
  font-size: 12px;
  font-weight: 800;
  fill: #1e293b;
}
.subchart-lbl {
  font-size: 6.5px;
  fill: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.subchart-legend {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.subchart-leg-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #64748b;
}
.subchart-leg-item b {
  color: #1e293b;
}
.subchart-list {
  flex: 1;
  min-width: 240px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.subchart-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  background: #f8fafc;
  border-radius: 8px;
}
.subchart-check {
  font-size: 15px;
  color: #cbd5e1;
  flex-shrink: 0;
}
.subchart-check.done {
  color: #10b981;
}
.subchart-row-info {
  flex: 1;
  min-width: 0;
}
.subchart-row-name {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
}
.subchart-row-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
}
.subchart-status {
  font-size: 9.5px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 4px;
  background: #eef2f7;
  color: #64748b;
}
.subchart-status.st-bajarilgan {
  background: rgba(16, 185, 129, 0.13);
  color: #0f9d76;
}
.subchart-status.st-jarayonda {
  background: rgba(245, 158, 11, 0.15);
  color: #b57209;
}
.subchart-status.st-yangi {
  background: rgba(59, 130, 246, 0.13);
  color: #3b82f6;
}
.subchart-date {
  font-size: 10px;
  color: #94a3b8;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.subchart-avatars {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.subchart-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #fff;
  margin-left: -6px;
}
.subchart-avatar:first-child {
  margin-left: 0;
}

/* Ochilish animatsiyasi */
.expand-enter-active,
.expand-leave-active {
  transition: opacity 0.25s ease, max-height 0.3s ease;
  overflow: hidden;
  max-height: 500px;
}
.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
}
.drilldown-info {
  min-width: 0;
  flex: 1;
}
.drilldown-name {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}
.dd-id {
  font-size: 10.5px;
  font-weight: 700;
  color: #94a3b8;
  background: #eef2f7;
  border-radius: 4px;
  padding: 1px 5px;
  margin-right: 6px;
  vertical-align: middle;
}

/* Meta qatori — barcha ma'lumot bitta qatorda, belgilangan chiplar */
.dd-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px 6px;
  margin-top: 5px;
}
.dd-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 260px;
  font-size: 10.5px;
  font-weight: 500;
  color: #64748b;
  background: #eef2f7;
  border-radius: 5px;
  padding: 2px 7px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dd-chip i {
  opacity: 0.75;
  flex-shrink: 0;
  font-size: 10px;
}
/* "Ijrochi:" / "Muallif:" kabi belgilar */
.dd-key {
  font-weight: 700;
  opacity: 0.7;
  flex-shrink: 0;
}
.dd-chip--exec {
  color: #0f9d76;
  background: rgba(16, 185, 129, 0.11);
}
.dd-chip--author {
  color: #6366f1;
  background: rgba(99, 102, 241, 0.1);
}
.dd-chip--ok {
  color: #10b981;
  background: rgba(16, 185, 129, 0.11);
  font-weight: 600;
}
.dd-days {
  font-size: 9.5px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 4px;
  white-space: nowrap;
  margin-left: 2px;
}
.dd-days--ok {
  background: rgba(16, 185, 129, 0.12);
  color: #0f9d76;
}
.dd-days--today {
  background: rgba(245, 158, 11, 0.15);
  color: #b57209;
}
.dd-days--over {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}
.dd-days--done {
  background: rgba(100, 116, 139, 0.12);
  color: #64748b;
}
.dd-days--muted {
  background: #f1f5f9;
  color: #cbd5e1;
}
.drilldown-assignees {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.drilldown-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #fff;
  margin-left: -6px;
}
.drilldown-avatar:first-child {
  margin-left: 0;
}
.drilldown-more {
  margin-left: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* EFFICIENCY */
.eff-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.eff-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.eff-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}
.eff-info {
  flex: 1;
  min-width: 0;
}
.eff-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.eff-name {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}
.eff-rate {
  font-size: 14px;
  font-weight: 800;
  flex-shrink: 0;
}
.eff-track {
  height: 7px;
  background: #eef2f7;
  border-radius: 4px;
  overflow: hidden;
  margin: 5px 0;
}
.eff-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.5s ease;
}
.eff-meta {
  display: flex;
  gap: 12px;
  font-size: 11px;
  color: #94a3b8;
  flex-wrap: wrap;
}
.eff-meta i {
  margin-right: 2px;
}
.eff-overdue {
  color: #ef4444;
}

/* SUBTASKS */
.subtask-summary {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.subtask-chip {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 6px;
  padding: 3px 9px;
}
.subtask-chip--accent {
  background: rgba(99, 102, 241, 0.12);
  color: #6366f1;
}
.sub-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
}
.sub-card {
  border: 1px solid #eef2f7;
  border-radius: 12px;
  padding: 12px 14px;
  background: #f8fafc;
}
.sub-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}
.sub-name {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  min-width: 0;
}
.sub-progress {
  display: flex;
  align-items: center;
  gap: 10px;
}
.sub-progress-track {
  flex: 1;
  height: 8px;
  background: #e9edf3;
  border-radius: 4px;
  overflow: hidden;
}
.sub-progress-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.5s ease;
}
.sub-progress-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  white-space: nowrap;
}

/* WORKLOAD */
.workload-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.workload-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 14px;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  transition: 0.2s;
}
.workload-row:hover {
  border-color: #c7d2fe;
}
.workload-row.overloaded {
  border-color: #fecaca;
  background: linear-gradient(90deg, #fff5f5, #fff);
}
.workload-user {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 220px;
  flex-shrink: 0;
}
.workload-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}
.workload-user-info {
  min-width: 0;
}
.workload-name {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}
.overload-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 700;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
  padding: 1px 6px;
  border-radius: 4px;
  margin-top: 2px;
}
.workload-bar-wrap {
  flex: 1;
  min-width: 100px;
}
.workload-bar {
  display: flex;
  height: 26px;
  border-radius: 6px;
  overflow: hidden;
  background: #f1f5f9;
  gap: 2px;
}
.workload-seg {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  min-width: 22px;
  transition: width 0.5s ease;
}
/* Subtask ijro segmenti — chiziqli (task ijrodan ajratilgan) */
.workload-seg--sub {
  color: #fff;
  background-color: var(--seg-color, #10b981);
  background-image: repeating-linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.35),
    rgba(255, 255, 255, 0.35) 3px,
    transparent 3px,
    transparent 7px
  );
}
.dot-striped {
  background-image: repeating-linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.55),
    rgba(255, 255, 255, 0.55) 2px,
    transparent 2px,
    transparent 4px
  ) !important;
}
.wl-sub-tag {
  margin-left: 3px;
  font-size: 9.5px;
  font-weight: 800;
  padding: 0 4px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.08);
}
.eff-sub {
  color: #6366f1;
}
.workload-stats {
  display: flex;
  gap: 6px;
  width: 170px;
  flex-shrink: 0;
  justify-content: flex-end;
}
.wl-chip {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 6px;
}
.wl-ijro {
  background: rgba(16, 185, 129, 0.12);
  color: #0f9d76;
}
.wl-malumot {
  background: rgba(148, 163, 184, 0.16);
  color: #64748b;
}

/* MATRIX */
.matrix-scroll {
  overflow-x: auto;
}
.matrix-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 4px;
  min-width: 480px;
}
.matrix-table th {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 6px;
  text-align: center;
}
.matrix-corner {
  text-align: left !important;
}
.matrix-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  min-width: 160px;
  max-width: 200px;
}
.matrix-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}
.matrix-cell {
  text-align: center;
  border-radius: 8px;
  font-size: 14px;
  padding: 10px;
  min-width: 70px;
}
.matrix-total,
.matrix-total-cell {
  text-align: center;
  font-weight: 800;
  color: #1e293b;
}

/* GANTT */
.gantt {
  position: relative;
}
.gantt-scale {
  position: relative;
  height: 20px;
  margin-left: 200px;
  border-bottom: 1px solid #eef2f7;
  margin-bottom: 8px;
}
.gantt-month {
  position: absolute;
  font-size: 10px;
  color: #94a3b8;
  transform: translateX(-50%);
  white-space: nowrap;
}
.gantt-rows {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.gantt-today {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 2px;
  background: #ef4444;
  margin-left: 200px;
  z-index: 3;
  pointer-events: none;
}
.gantt-today-label {
  position: absolute;
  top: -2px;
  left: 3px;
  font-size: 9px;
  font-weight: 700;
  color: #ef4444;
  white-space: nowrap;
}
.gantt-row {
  display: flex;
  align-items: center;
  height: 28px;
}
.gantt-row-label {
  width: 200px;
  flex-shrink: 0;
  font-size: 12px;
  color: #475569;
  padding-right: 12px;
}
.gantt-track {
  position: relative;
  flex: 1;
  height: 100%;
  background: repeating-linear-gradient(90deg, #f8fafc, #f8fafc 1px, transparent 1px, transparent 60px);
  border-radius: 4px;
}
.gantt-bar {
  position: absolute;
  top: 5px;
  height: 18px;
  border-radius: 5px;
  min-width: 6px;
  display: flex;
  align-items: center;
  padding: 0 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  transition: 0.2s;
}
.gantt-bar:hover {
  transform: scaleY(1.15);
  z-index: 2;
}
.gantt-overdue {
  background-image: repeating-linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.25),
    rgba(255, 255, 255, 0.25) 4px,
    transparent 4px,
    transparent 8px
  ) !important;
}
.gantt-bar-text {
  font-size: 9px;
  color: #fff;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
}

/* ============ DATA TABLE (zamonaviy) ============ */
.tbl-panel {
  padding: 20px 22px 22px;
}
.tbl2-toolbar {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}
.tbl2-search {
  position: relative;
  display: flex;
  align-items: center;
  background: #f6f8fc;
  border: 1px solid #e6e9f0;
  border-radius: 11px;
  padding: 0 12px;
  height: 40px;
  flex: 1;
  min-width: 200px;
  transition: 0.2s;
}
.tbl2-search:focus-within {
  border-color: #6366f1;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}
.tbl2-search i {
  color: #94a3b8;
  font-size: 13px;
}
.tbl2-search input {
  border: none;
  outline: none;
  background: transparent;
  padding: 0 8px;
  font-size: 13px;
  width: 100%;
  height: 100%;
}
.tbl2-search button {
  border: none;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  font-size: 12px;
}
.tbl2-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.tbl2-field--grow {
  flex: 1;
  min-width: 160px;
}
.tbl2-field > label {
  font-size: 10.5px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding-left: 2px;
}
.tbl2-select {
  border: 1px solid #e6e9f0;
  background: #f6f8fc;
  border-radius: 11px;
  padding: 0 12px;
  height: 40px;
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
  cursor: pointer;
  outline: none;
  min-width: 130px;
  transition: 0.2s;
}
.tbl2-select:focus {
  border-color: #6366f1;
  background: #fff;
}

/* Rol segmenti */
.role-seg {
  display: inline-flex;
  background: #f1f5f9;
  border: 1px solid #e6e9f0;
  border-radius: 11px;
  padding: 3px;
  height: 40px;
}
.role-seg.disabled {
  opacity: 0.5;
}
.role-seg-btn {
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  padding: 0 12px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: 0.18s;
  white-space: nowrap;
}
.role-seg-btn:not(:disabled):hover {
  color: #1e293b;
}
.role-seg-btn.active {
  background: #fff;
  color: #4f46e5;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.08);
}
.role-seg-btn.active.seg-danger {
  color: #ef4444;
}
.role-seg-btn:disabled {
  cursor: not-allowed;
}
.tbl2-count {
  font-size: 12px;
  color: #94a3b8;
  white-space: nowrap;
  padding-bottom: 10px;
}
.tbl2-count b {
  color: #1e293b;
  font-size: 15px;
}

/* Jadval */
.tbl2-scroll {
  overflow-x: auto;
  border: 1px solid #eef1f6;
  border-radius: 14px;
}
.tbl2 {
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
}
.tbl2 thead th {
  position: sticky;
  top: 0;
  text-align: left;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #64748b;
  background: #f7f9fc;
  padding: 13px 14px;
  border-bottom: 1px solid #e6e9f0;
  white-space: nowrap;
  z-index: 1;
}
.tbl2 th.sortable {
  cursor: pointer;
  user-select: none;
  transition: color 0.15s;
}
.tbl2 th.sortable:hover {
  color: #4f46e5;
}
.tbl2 th.sortable i {
  margin-left: 3px;
  font-size: 10px;
}
.sort-idle {
  opacity: 0.35;
}
.tbl2-id {
  width: 52px;
  color: #94a3b8;
  font-weight: 700;
  font-size: 12px;
  text-align: center;
}
.tbl2-num {
  width: 92px;
  white-space: nowrap;
}
.tbl2-num-badge {
  display: inline-block;
  font-size: 11.5px;
  font-weight: 700;
  color: #6366f1;
  background: #eef2ff;
  padding: 2px 9px;
  border-radius: 20px;
  font-variant-numeric: tabular-nums;
}
.tbl2 tbody td {
  padding: 11px 14px;
  font-size: 13px;
  color: #334155;
  border-bottom: 1px solid #f4f6fa;
  vertical-align: middle;
}
.tbl2 tbody tr {
  transition: background 0.15s;
}
.tbl2 tbody tr:last-child td {
  border-bottom: none;
}
.tbl2 tbody tr:hover {
  background: #f8faff;
}
.tbl2-overdue {
  background: rgba(239, 68, 68, 0.035);
}
.tbl2-name {
  max-width: 300px;
  position: relative;
  padding-left: 20px !important;
}
.tbl2-name span.text-truncate {
  display: block;
  font-weight: 500;
  color: #1e293b;
}
.tbl2-status-bar {
  position: absolute;
  left: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 22px;
  border-radius: 3px;
}
.tbl2-users {
  display: flex;
  align-items: center;
}
.tbl2-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #fff;
  margin-left: -7px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
}
.tbl2-users .tbl2-avatar:first-child {
  margin-left: 0;
}
.tbl2-more {
  margin-left: 4px;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
}
.tbl2-author {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 150px;
  font-size: 12.5px;
  font-weight: 500;
  color: #475569;
}
.tbl2-author .tbl2-avatar {
  margin-left: 0;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
}
.tbl2-date {
  white-space: nowrap;
  color: #64748b;
  font-variant-numeric: tabular-nums;
}
.status-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;
}
.td-empty {
  color: #cbd5e1;
}
.td-date {
  white-space: nowrap;
  color: #64748b;
}
.days-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
  white-space: nowrap;
}
.days-ok {
  background: rgba(16, 185, 129, 0.12);
  color: #0f9d76;
}
.days-today {
  background: rgba(245, 158, 11, 0.14);
  color: #b57209;
}
.days-over {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}
.days-done {
  background: rgba(100, 116, 139, 0.12);
  color: #64748b;
}
.subtask-progress {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 110px;
}
.subtask-progress-bar {
  flex: 1;
  height: 6px;
  background: #eef2f7;
  border-radius: 3px;
  overflow: hidden;
}
.subtask-progress-fill {
  height: 100%;
  background: #6366f1;
  border-radius: 3px;
}
.subtask-progress-text {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  white-space: nowrap;
}

/* Ijrochi ustuni: avatar + ism */
.tbl2-ijro {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 180px;
}
.tbl2-ijro .tbl2-users {
  flex-shrink: 0;
}
.tbl2-ijro-name {
  font-size: 12.5px;
  font-weight: 500;
  color: #475569;
  min-width: 0;
}

/* Subtask toggle tugmasi */
.subtask-toggle {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 4px 9px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.18s ease;
}
.subtask-toggle:hover {
  border-color: #c7d2fe;
  background: #eef2ff;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.12);
}
.subtask-toggle.open {
  border-color: #a5b4fc;
  background: #eef2ff;
}
.subtask-toggle-ico {
  font-size: 12px;
  color: #6366f1;
}
.subtask-toggle.full .subtask-toggle-ico {
  color: #10b981;
}
.subtask-toggle-count {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  font-variant-numeric: tabular-nums;
}
.subtask-toggle-arrow {
  font-size: 10px;
  color: #94a3b8;
  transition: transform 0.2s ease;
}
.subtask-toggle.open .subtask-toggle-arrow {
  transform: rotate(180deg);
  color: #6366f1;
}

/* Ochilgan subtask qatori — dashboard uslubidagi panel */
.tbl2-expanded > td {
  border-bottom-color: transparent !important;
}
.tbl2-subrow > td {
  background: #f6f8fc;
  padding: 0 !important;
  box-shadow: inset 3px 0 0 #a5b4fc;
  /* colspan katak jadvalни kengaytirmasin — kontent ichida o'raladi */
  max-width: 0;
}
/* Yagona karta: sarlavha + subtasklar bitta kartada */
.subpanel {
  margin: 14px 18px 16px 30px;
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.05);
  overflow: hidden;
  animation: subpanel-in 0.25s ease;
}
@keyframes subpanel-in {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Sarlavha bloki — kartaning yuqori qismi */
.subpanel-head {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 14px 18px;
  border-bottom: 1px solid #f1f5f9;
}
.subpanel-donut {
  width: 68px;
  height: 68px;
  flex-shrink: 0;
}
.subpanel-donut svg {
  width: 100%;
  height: 100%;
}
.subpanel-donut-num {
  font-size: 15px;
  font-weight: 800;
  fill: #1e293b;
}
.subpanel-donut-lbl {
  font-size: 7px;
  font-weight: 600;
  fill: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.subpanel-main {
  flex: 1;
  min-width: 0;
}
.subpanel-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
}
.subpanel-title i {
  color: #6366f1;
}
.subpanel-title-count {
  font-size: 11px;
  font-weight: 700;
  color: #6366f1;
  background: #eef2ff;
  padding: 2px 9px;
  border-radius: 20px;
}
.subpanel-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 9px 0;
}
.substat {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 3px 10px;
  border-radius: 20px;
}
.substat b {
  color: #1e293b;
}
.substat--done { background: rgba(16, 185, 129, 0.12); color: #059669; }
.substat--prog { background: rgba(245, 158, 11, 0.14); color: #d97706; }
.substat--new { background: rgba(59, 130, 246, 0.12); color: #3b82f6; }
.substat--over { background: rgba(239, 68, 68, 0.12); color: #ef4444; }
.subpanel-progress {
  height: 7px;
  background: #eef2f7;
  border-radius: 4px;
  overflow: hidden;
}
.subpanel-progress-fill {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, #6366f1, #10b981);
  transition: width 0.4s ease;
}

/* Subtasklar — yagona kartaning ichida tekis qatorlar (alohida karta emas) */
.subcards {
  display: flex;
  flex-direction: column;
  padding: 4px 16px 8px;
}
.subcard {
  padding: 12px 12px;
  border-left: 3px solid #cbd5e1;
  border-bottom: 1px solid #f4f6f9;
  border-radius: 0 8px 8px 0;
  transition: background 0.18s ease;
}
.subcard:last-child {
  border-bottom: none;
}
.subcard:hover {
  background: #f8fafc;
}
.subcard.is-done { border-left-color: #10b981; }
.subcard.is-prog { border-left-color: #f59e0b; }
.subcard.is-new { border-left-color: #3b82f6; }
.subcard.is-over { border-left-color: #ef4444; }
.subcard-top {
  display: flex;
  align-items: center;
  gap: 8px;
}
.subcard-check {
  font-size: 14px;
  flex-shrink: 0;
  color: #cbd5e1;
}
.subcard.is-done .subcard-check { color: #10b981; }
.subcard-name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.subcard-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
  white-space: nowrap;
  flex-shrink: 0;
  background: #f1f5f9;
  color: #64748b;
}
.subcard.is-done .subcard-badge { background: rgba(16, 185, 129, 0.12); color: #059669; }
.subcard.is-prog .subcard-badge { background: rgba(245, 158, 11, 0.14); color: #d97706; }
.subcard.is-new .subcard-badge { background: rgba(59, 130, 246, 0.12); color: #3b82f6; }
.subcard.is-over .subcard-badge { background: rgba(239, 68, 68, 0.12); color: #ef4444; }
.subcard-desc {
  margin: 7px 0 0;
  font-size: 11.5px;
  line-height: 1.45;
  color: #64748b;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  /* uzun matn/so'z qatorni kengaytirmasin */
  overflow-wrap: anywhere;
  word-break: break-word;
}
.subcard-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
  padding-top: 9px;
  border-top: 1px dashed #eef2f7;
}
.subcard-people {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  min-width: 0;
}
.subcard-person {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-weight: 600;
  color: #334155;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  padding: 2px 8px 2px 2px;
  border-radius: 20px;
}
.subcard-ava {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  object-fit: cover;
}
.subcard-noexec {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.08);
  padding: 2px 9px;
  border-radius: 20px;
}
.subcard-info-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  color: #6366f1;
  background: #eef2ff;
  padding: 2px 8px;
  border-radius: 20px;
}
.subcard-deadline {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  white-space: nowrap;
  flex-shrink: 0;
}
.subcard-deadline.over {
  color: #ef4444;
}
.subcard-days {
  font-weight: 600;
}
.td-empty-row {
  text-align: center;
  padding: 40px !important;
  color: #cbd5e1;
}

@media (max-width: 768px) {
  .dash-body {
    padding: 16px;
  }
  .workload-user {
    width: 140px;
  }
  .workload-stats {
    display: none;
  }
  .gantt-row-label,
  .gantt-scale,
  .gantt-today {
    margin-left: 0;
  }
  .gantt-row-label {
    width: 100px;
  }
}
</style>
